import { useState, useEffect, useMemo, useRef, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  collection, onSnapshot, doc, getDoc, runTransaction, serverTimestamp,
  type Transaction, type Firestore,
} from 'firebase/firestore';
import { createdStamp, logActivity } from '../utils/activity';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { useNewParam } from '../hooks/useNewParam';
import { Button, Modal, ModalBanner, ConfirmModal, FormField, Input, EntityCombobox, Dropdown, PageTabs, TotalsFigures } from '../components/ui';
import { useGridKeyNav } from '../hooks/useGridKeyNav';
import { OptionCombobox } from '../components/ui/OptionCombobox';
import { rememberLooseOptions } from '../utils/looseOptions';
import {
  downloadImportWorkbook, loadWorkbook, readSheetRows, matchOption, cleanNum, parseImportDate, xlsxUtcDate,
  XLSX_GROUP_ARGB, type XlsxCol,
} from '../utils/excelImport';
import { HeaderSearch, DateRangeButton, StatChips, IconImportSm, IconExportSm } from '../components/HeaderControls';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { useSettings } from '../hooks/useSettings';
import { downloadCSV } from '../utils/csv';
import { fmtMoney } from '../utils/currency';
import { computeDateWindow, inWindow, type DateFilterKey } from '../utils/ledgerReports';
import {
  buildEntry, postEntry, entryId, linesMemoIn, linesPurchaseInvoice, linesBrokerAccrual,
} from '../utils/ledger';
import type {
  SupplierMemo, SupplierMemoLineItem, Entity, Sku, CodeEventType,
  PurchaseLineItem, PaymentStatus, PurchaseType, DiamondLab, TransactionCategory,
  DiamondShape, DiamondColor, DiamondClarity, BusinessSettings,
} from '../types';

// ── Option lists (per-page, matching the Purchase / Sales line-item forms) ──
const SHAPES: DiamondShape[]      = ['Round','Princess','Oval','Cushion','Emerald','Pear','Marquise','Radiant','Asscher','Heart','Other'];
const COLORS: DiamondColor[]      = ['D','E','F','G','H','I','J','K','L','M','Fancy'];
const CLARITIES: DiamondClarity[] = ['FL','IF','VVS1','VVS2','VS1','VS2','SI1','SI2','I1','I2','I3'];
const LABS: DiamondLab[]          = ['GIA','IGI','HRD','AGS','GCAL','EGL','None'];
const CUT_GRADES = ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor']; // also used for Polish/Symmetry
const FLUORESCENCE_GRADES = ['None', 'Faint', 'Medium', 'Strong', 'Very Strong'];

// ── Helpers (per-page, matching convention) ──────────────────────────────
function todayISO() { return toDateStr(new Date()); }   // local day, not UTC — see toDateStr
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
function num(s: string) { return parseFloat(s) || 0; }
// Local calendar day — never toISOString(), which is UTC: in any timezone
// ahead of UTC (India, Europe…) local midnight is still the previous day in
// UTC, so every computed date (due dates, today, filter ranges) came out a day early.
function toDateStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDaysToDate(base: string, n: number): string {
  const d = base ? new Date(base + 'T00:00:00') : new Date();
  if (isNaN(d.getTime())) return toDateStr(new Date());
  d.setDate(d.getDate() + n);
  return toDateStr(d);
}
// Deterministic Firestore doc ID for a SKU string — mirrors Purchase.tsx's
// slugifySku (and this file's own normalizeCode for Code).
function slugifySku(sku: string): string {
  return sku.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}
// createdAt is written via serverTimestamp() (a Firestore Timestamp once
// synced back) even though the app's own types declare it as `string` — a
// plain .localeCompare() on it throws and crashes the page the moment two
// rows share the same date and the sort's tie-breaker actually runs. Reads
// either shape (Timestamp instance, plain {seconds}, or an ISO string)
// into sortable millis instead. Mirrors Dashboard.tsx's own toMillis.
function toMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') return new Date(v).getTime();
  const obj = v as { toMillis?: () => number; seconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') return obj.seconds * 1000;
  return 0;
}
const fmtDate = (s?: string) =>
  s ? new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
// Memo No is stored in full, but the list only shows the prefix + last 4
// digits to keep the column compact — same convention as Purchase's Lot No.
function memoNoDisplay(memoNo: string) {
  if (!memoNo) return '—';
  const m = memoNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : memoNo.slice(-4);
}
// Full "Mon, Jan 5, 2026 · in 30 days" label for the Return-by hint —
// mirrors formatDueDate on the Purchase / Sales invoice headers.
function formatReturnBy(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  const label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff  = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  const when  = diff === 0 ? 'today' : diff > 0 ? `in ${diff} day${diff !== 1 ? 's' : ''}` : `${Math.abs(diff)}d overdue`;
  return `${label} · ${when}`;
}
function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}
function fmtInvoiceNo(prefix: string, count: number) {
  return `${prefix}-${new Date().getFullYear()}-${String(count).padStart(4, '0')}`;
}
async function nextFreeCode(tx: Transaction, db: Firestore, cursor: { n: number }, reserved: Set<string>): Promise<string> {
  for (let i = 0; i < 300; i++) {
    cursor.n += 1;
    const cand = String(cursor.n);
    const id = normalizeCode(cand);
    if (reserved.has(id)) continue;
    const s = await tx.get(doc(db, 'itemCodes', id));
    if (!s.exists()) { reserved.add(id); return cand; }
  }
  throw new Error('Could not auto-generate a unique Code — enter one manually.');
}
const heldOf = (m: SupplierMemo) =>
  round2((m.lines ?? []).filter(l => l.status === 'held').reduce((s, l) => s + (l.costUSD ?? 0), 0));

// Per-consignment figures for the list table — all derived from its lines,
// so they're right for memos created before any of these columns existed.
function memoStats(m: SupplierMemo) {
  const lines = m.lines ?? [];
  const sum = (ls: SupplierMemoLineItem[], f: (l: SupplierMemoLineItem) => number) => round2(ls.reduce((s, l) => s + f(l), 0));
  const held = lines.filter(l => l.status === 'held');
  return {
    looseN:      lines.filter(l => l.itemType === 'loose').length,
    certN:       lines.filter(l => l.itemType === 'certified').length,
    heldN:       held.length,
    totalCt:     sum(lines, l => l.carat ?? 0),
    heldCt:      sum(held, l => l.carat ?? 0),
    totalAmt:    sum(lines, l => l.costUSD ?? 0),
    heldAmt:     sum(held, l => l.costUSD ?? 0),
    boughtAmt:   sum(lines.filter(l => l.status === 'purchased'), l => l.costUSD ?? 0),
    returnedAmt: sum(lines.filter(l => l.status === 'returned'), l => l.costUSD ?? 0),
  };
}
// Whole days from today (local) to a YYYY-MM-DD date — negative = past.
function daysUntil(iso: string): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((new Date(iso + 'T00:00:00').getTime() - today.getTime()) / 86400000);
}

const PAGE_SIZE = 50;

// ── Pagination (same pattern as Purchase/Sales/Inventory/Entity/Memos/AP/AR/SalesReturns) ──
function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);
  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3)         pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> memo{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

// ── Line-item card helpers — mirror the Add Purchase Invoice line items ──
// (the .line-item-* CSS classes are global in index.css; only these small
// helpers are duplicated per-page, matching this codebase's convention.)
function IconTrash() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M2 3.5h10M5.5 3.5V2.5h3v1M4 3.5l.667 8h4.666L10 3.5"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
const TI: CSSProperties = {
  width: '100%', minWidth: 0, padding: '6px 9px', fontSize: 11, height: 32,
  border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)',
  background: 'var(--surface)', color: 'var(--text-1)',
  outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
};
// Shape/Color/Clarity all use this — a plain <select> keeps its native OS
// chrome unless appearance is reset, and that native rendering doesn't
// respect border-radius the same way an <input> does, so selects looked
// like they had a different corner radius from the inputs next to them in
// the same grid. Mirrors Purchase.tsx's TS exactly.
const TS: CSSProperties = {
  ...TI,
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2394A3B8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 7px center',
  paddingRight: 20,
};
function LineField({ label, children, style }: { label: string; children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="line-item-field" style={style}>
      <label>{label}</label>
      {children}
    </div>
  );
}
function RowTypePill({ value, onChange }: { value: 'certified' | 'loose'; onChange: (t: 'certified' | 'loose') => void }) {
  return (
    <div style={{ display: 'flex', gap: 1, background: 'var(--surface-2)', borderRadius: 5, padding: 2 }}>
      {(['certified', 'loose'] as const).map(t => {
        const active = value === t;
        return (
          <button key={t} type="button" onClick={() => onChange(t)} style={{
            fontSize: 10, padding: '2px 7px', border: 'none', cursor: 'pointer',
            borderRadius: 3, fontFamily: 'inherit', fontWeight: active ? 700 : 400,
            background: active ? (t === 'certified' ? 'var(--primary)' : '#b45309') : 'transparent',
            color: active ? '#fff' : 'var(--text-3)', transition: 'all 0.12s', whiteSpace: 'nowrap',
          }}>
            {t === 'certified' ? 'Cert' : 'Loose'}
          </button>
        );
      })}
    </div>
  );
}
// Mirrors Purchase.tsx's TermsSelector — quick day-count chips + a free
// number input, both driving the same value.
// Just the days box — no preset 30d/45d/60d/90d buttons (same as Purchase.tsx).
function TermsSelector({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <input type="number" min={1} value={value}
        onChange={e => onChange(parseInt(e.target.value) || 1)}
        className="terms-input" style={{ width: 58, textAlign: 'center', fontWeight: 700 }} />
      <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 500 }}>days</span>
    </div>
  );
}

// ── Transaction category (Books/Cash × Real/Dummy) — mirrors Purchase.tsx's
// own CategoryPicker/CATEGORY_META exactly, duplicated per this codebase's
// per-page convention. ───────────────────────────────────────────────────
function IconCatBooksReal() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M4.2 7.2l2 2 3.6-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCatCashReal() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M3.6 3.6l6.8 6.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconCatBooksDummy() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path d="M3.5 1.5h4.5l2.5 2.5v8a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1v-9.5a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M8 1.5v2.5h2.5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M5 8.3c.3-.5 1-.6 1.4-.3.5.3.5.9 0 1.2-.6.3-.6.9 0 1.2.4.3 1.1.2 1.4-.3M6.6 7v5" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
  </svg>;
}
const CATEGORY_META: Record<TransactionCategory, { label: string; short: string; color: string; icon: ReactNode }> = {
  'books-real':  { label: 'By Books & Real',  short: 'By Books', color: 'var(--success)', icon: <IconCatBooksReal /> },
  'cash-real':   { label: 'Cash & Real',      short: 'Cash',     color: 'var(--danger)',  icon: <IconCatCashReal /> },
  'books-dummy': { label: 'By Books & Dummy', short: 'Dummy',    color: 'var(--text-2)',  icon: <IconCatBooksDummy /> },
};
const CATEGORY_ORDER: TransactionCategory[] = ['books-real', 'cash-real', 'books-dummy'];
function IconChevronDownSm() {
  return <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
    <path d="M3 5.5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function CategoryPicker({ value, onChange }: { value: TransactionCategory | ''; onChange: (v: TransactionCategory | '') => void }) {
  const current = value ? CATEGORY_META[value] : null;
  return (
    <Dropdown
      panelClassName="icon-only"
      trigger={() => (
        <button type="button" title={current?.label ?? 'Select category'} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
          height: 22, padding: '0 6px', borderRadius: 5, boxSizing: 'border-box', flexShrink: 0,
          border: `1.5px solid ${current ? current.color : 'var(--border)'}`,
          background: current ? `color-mix(in srgb, ${current.color} 14%, transparent)` : 'var(--surface)',
          color: current ? current.color : 'var(--text-3)',
          cursor: 'pointer', fontFamily: 'inherit',
        }}>
          {current ? current.icon : <IconCatBooksReal />}
          <span style={{ color: 'var(--text-3)', display: 'flex' }}><IconChevronDownSm /></span>
        </button>
      )}
    >
      {CATEGORY_ORDER.map(key => {
        const meta = CATEGORY_META[key];
        return (
          <button
            key={key} type="button" className="dropdown-item" title={meta.label}
            style={{ color: meta.color, justifyContent: 'center' }}
            onClick={() => onChange(key)}
          >
            {meta.icon}
          </button>
        );
      })}
    </Dropdown>
  );
}

// ── DescriptionCombobox — free text + a type-ahead list of the saved
// descriptions from Settings › Custom Options (BusinessSettings.
// saleLineDescriptions — the same list Sales/Memos/Purchase's own line
// items already share). Mirrors Purchase.tsx's own DescriptionCombobox.
function DescriptionCombobox({ value, onChange, options }: {
  value: string; onChange: (v: string) => void; options: string[];
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function update() {
      const r = inputRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) });
    }
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const q = value.trim().toLowerCase();
  const list = options.filter(o => !q || o.toLowerCase().includes(q)).slice(0, 40);
  const hasList = options.length > 0 && list.length > 0;

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        style={{ ...TI, width: '100%' }}
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onClick={() => options.length > 0 && setOpen(true)}
        onKeyDown={e => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); } }}
        placeholder={options.length > 0 ? 'Add a note / pick…' : 'Add a note…'}
      />
      {open && pos && hasList && createPortal(
        <div ref={panelRef} className="dropdown-panel"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: 240, overflowY: 'auto' }}>
          {list.map(o => (
            <button
              key={o} type="button" className="dropdown-item"
              onClick={() => { onChange(o); setOpen(false); inputRef.current?.blur(); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', whiteSpace: 'normal' }}
            >
              {o}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

// ── Receive Consignment — same shape as Add Purchase Invoice ────────────────
// Header block, Loose / Certified tabs over an Excel-style grid (red cells
// for empty required fields), plus an Excel import that can carry many
// consignments in one file. A consignment still isn't owned stock: receiving
// writes only the `supplierMemos` doc (+ memo_in journal) — no `inventory`
// doc, no Code — until a line is converted to a purchase (ConvertModal),
// which starts from everything captured here.

type MemoRowType = 'loose' | 'certified';

interface MemoRow {
  _key:     string;
  itemType: MemoRowType;
  category: TransactionCategory | '';
  sku: string; description: string;
  shape: string; size: string; subSize: string; carat: string;
  color: string; clarity: string;
  lab: string; certificateNo: string;
  cut: string; polish: string; symmetry: string; fluorescence: string;
  measLength: string; measWidth: string; measDepth: string;
  // Certified List Price/ct + Discount % ↔ Price/ct — Purchase's
  // resolveCertPrice rule ('discount' drives Price/ct, 'price' drives Discount %).
  listPrice: string; discount: string;
  priceMode: 'discount' | 'price' | undefined;
  pricePerCt: string;   // the consignment price per carat — line cost = pricePerCt × carat
  // Consignment header — only used by the Excel import, where every row says
  // which consignment it belongs to. Blank for rows typed in the Receive form.
  fromEntityId: string; supplierRef: string; date: string; returnBy: string;
}

let _memoRowKey = 0;
function newMemoRow(itemType: MemoRowType): MemoRow {
  return {
    _key: `m${++_memoRowKey}`, itemType, category: 'books-real',
    sku: '', description: '', shape: '', size: '', subSize: '', carat: '',
    color: '', clarity: '', lab: '', certificateNo: '',
    cut: '', polish: '', symmetry: '', fluorescence: '', measLength: '', measWidth: '', measDepth: '',
    listPrice: '', discount: '', priceMode: undefined, pricePerCt: '',
    fromEntityId: '', supplierRef: '', date: '', returnBy: '',
  };
}

function resolveMemoPrice(r: MemoRow): MemoRow {
  if (r.itemType !== 'certified') return r;
  const list = num(r.listPrice);
  if (r.priceMode === 'price') {
    if (list <= 0) return r;
    return { ...r, discount: String(+(((num(r.pricePerCt) - list) / list) * 100).toFixed(4)) };
  }
  return list > 0 ? { ...r, pricePerCt: String(+(list * (1 + num(r.discount) / 100)).toFixed(4)) } : r;
}
const memoRowCost = (r: MemoRow) => round2(num(r.pricePerCt) * num(r.carat));

// ── Column spec — drives the on-screen grid AND the Excel sample/import ──
type MemoField = Exclude<keyof MemoRow, '_key' | 'itemType' | 'priceMode' | 'fromEntityId'> | 'company' | 'totalAmount';
type MemoListKey = 'company' | 'category' | 'shape' | 'looseColor' | 'looseClarity' | 'certColor' | 'certClarity' | 'lab' | 'grade' | 'fluor';
interface MemoCol extends XlsxCol<MemoField> {
  list?:     MemoListKey;
  required?: boolean;
  /** Part of the consignment header — required only when a file is split into consignments. */
  headerCol?: boolean;
  placeholder?: string;
}

const memoTotalFormula = (r: number, col: (f: MemoField) => string) =>
  `IF(OR(${col('carat')}${r}="",${col('pricePerCt')}${r}=""),"",ROUND(${col('carat')}${r}*${col('pricePerCt')}${r},2))`;

const MEMO_HEADER_COLS: MemoCol[] = [
  { header: 'Company Name',  field: 'company',     width: 24, list: 'company', headerCol: true, required: true, headerArgb: XLSX_GROUP_ARGB,
    aliases: ['company', 'supplier', 'supplier name'],
    note: 'Supplier — must match a company saved in DiaWolt. Rows with the same Company + Their Ref + Received Date become one consignment.' },
  { header: 'Their Ref',     field: 'supplierRef', width: 16, headerCol: true, headerArgb: XLSX_GROUP_ARGB,
    aliases: ['their memo', 'their memo no', 'their ref no', 'supplier ref', 'memo no', 'ref'] },
  { header: 'Received Date', field: 'date',        width: 14, headerCol: true, required: true, date: true, headerArgb: XLSX_GROUP_ARGB,
    aliases: ['date', 'receive date'], note: 'Any Excel date, or typed as day/month/year (01/09/2026)' },
  { header: 'Return By',     field: 'returnBy',    width: 14, headerCol: true, date: true, headerArgb: XLSX_GROUP_ARGB,
    aliases: ['return by date', 'return date'], note: 'Optional — when the goods must go back' },
];

const MEMO_LOOSE_COLS: MemoCol[] = [
  { header: 'Category',     field: 'category',    width: 18, list: 'category', note: 'Blank = By Books & Real' },
  { header: 'SKU',          field: 'sku',         width: 10, aliases: ['item no', 'item number'], note: 'Optional now — needed when converting to a purchase' },
  { header: 'Description',  field: 'description', width: 36 },
  { header: 'Shape',        field: 'shape',       width: 12, list: 'shape', required: true, note: 'Required' },
  { header: 'Size',         field: 'size',        width: 12, placeholder: '+2' },
  { header: 'Sub Size',     field: 'subSize',     width: 10, placeholder: '+1.5', aliases: ['subsize'] },
  { header: 'Carat',        field: 'carat',       width: 9,  numeric: true, required: true, placeholder: '0.00', note: 'Required' },
  { header: 'Price/ct',     field: 'pricePerCt',  width: 11, numeric: true, required: true, placeholder: '0.00', aliases: ['price per ct', 'price', 'cost/ct', 'cost per ct'], note: "Required. The supplier's price per ct" },
  { header: 'Color',        field: 'color',       width: 9,  list: 'looseColor', required: true, note: 'Required' },
  { header: 'Clarity',      field: 'clarity',     width: 9,  list: 'looseClarity', required: true, note: 'Required' },
  { header: 'Total Amount', field: 'totalAmount', width: 13, numeric: true, aliases: ['amount', 'cost', 'total cost'], formula: memoTotalFormula },
];

const MEMO_CERT_COLS: MemoCol[] = [
  { header: 'Category',      field: 'category',      width: 18, list: 'category', note: 'Blank = By Books & Real' },
  { header: 'Description',   field: 'description',   width: 36 },
  { header: 'Lab',           field: 'lab',           width: 9,  list: 'lab', required: true, note: 'Required' },
  { header: 'Cert No',       field: 'certificateNo', width: 14, aliases: ['certificate no', 'cert'], note: 'Required unless Lab = None' },
  { header: 'Shape',         field: 'shape',         width: 12, list: 'shape', required: true, note: 'Required' },
  { header: 'Carat',         field: 'carat',         width: 9,  numeric: true, required: true, placeholder: '0.00', note: 'Required' },
  { header: 'List Price/ct', field: 'listPrice',     width: 13, numeric: true, aliases: ['list price', 'list price per ct'] },
  { header: 'Discount %',    field: 'discount',      width: 11, numeric: true, aliases: ['disc'], note: 'Negative = discount, e.g. -35' },
  { header: 'Price/ct',      field: 'pricePerCt',    width: 11, numeric: true, required: true, placeholder: '0.00', aliases: ['price per ct', 'price', 'cost/ct', 'cost per ct'], note: 'Required. Or leave blank and give List Price + Discount %' },
  { header: 'Total Amount',  field: 'totalAmount',   width: 13, numeric: true, aliases: ['amount', 'cost', 'total cost'], formula: memoTotalFormula },
  { header: 'Color',         field: 'color',         width: 9,  list: 'certColor', required: true, note: 'Required' },
  { header: 'Clarity',       field: 'clarity',       width: 9,  list: 'certClarity', required: true, note: 'Required' },
  { header: 'Cut',           field: 'cut',           width: 11, list: 'grade' },
  { header: 'Polish',        field: 'polish',        width: 11, list: 'grade' },
  { header: 'Symmetry',      field: 'symmetry',      width: 11, list: 'grade' },
  { header: 'Fluorescence',  field: 'fluorescence',  width: 13, list: 'fluor', aliases: ['fluor'] },
  { header: 'Length',        field: 'measLength',    width: 9,  numeric: true, aliases: ['l'] },
  { header: 'Width',         field: 'measWidth',     width: 9,  numeric: true, aliases: ['w'] },
  { header: 'Depth',         field: 'measDepth',     width: 9,  numeric: true, aliases: ['d'] },
];

const memoCols = (t: MemoRowType, withHeader: boolean) =>
  [...(withHeader ? MEMO_HEADER_COLS : []), ...(t === 'loose' ? MEMO_LOOSE_COLS : MEMO_CERT_COLS)];

function memoLists(settings: BusinessSettings | null, companyNames: string[] = []): Record<MemoListKey, string[]> {
  return {
    company:      companyNames,
    category:     CATEGORY_ORDER.map(c => CATEGORY_META[c].label),
    shape:        SHAPES,
    // Loose vs certified Color/Clarity scales differ — same as Purchase.
    looseColor:   settings?.looseColorOptions?.length ? settings.looseColorOptions : COLORS,
    looseClarity: settings?.looseClarityOptions?.length ? settings.looseClarityOptions : CLARITIES,
    certColor:    COLORS,
    certClarity:  CLARITIES,
    lab:          LABS,
    grade:        CUT_GRADES,
    fluor:        FLUORESCENCE_GRADES,
  };
}

// ImportField-style mapping: the 'company' column edits fromEntityId.
const memoKey = (f: MemoField): keyof MemoRow | null =>
  f === 'company' ? 'fromEntityId' : f === 'totalAmount' ? null : f;

// "Started" = something typed beyond the pre-filled Category.
function memoRowStarted(r: MemoRow): boolean {
  return [...MEMO_HEADER_COLS, ...(r.itemType === 'loose' ? MEMO_LOOSE_COLS : MEMO_CERT_COLS)]
    .some(c => !c.formula && c.field !== 'category' && String(r[memoKey(c.field)!] ?? '').trim());
}
function memoCellMissing(r: MemoRow, c: MemoCol, withHeader: boolean): boolean {
  if (c.field === 'certificateNo') return r.lab !== 'None' && !r.certificateNo.trim();
  if (!c.required || (c.headerCol && !withHeader)) return false;
  const v = String(r[memoKey(c.field)!] ?? '').trim();
  return !v || ((c.field === 'carat' || c.field === 'pricePerCt') && num(v) <= 0);
}

// ── Grid styles — Purchase.tsx's Excel-style grid (GTH/GTD/GTD_TEXT there) ──
const GTD: CSSProperties = { padding: 0, verticalAlign: 'middle' };
const GTD_TEXT: CSSProperties = { ...GTD, padding: '6px 8px', fontSize: 11 };
const GTH: CSSProperties = {
  ...GTD_TEXT, fontSize: 9.5, fontWeight: 700, color: 'var(--text-2)', textAlign: 'left', whiteSpace: 'nowrap',
  background: 'var(--surface-2)', borderBottom: '1.5px solid var(--border)', position: 'sticky', top: 0, zIndex: 2,
};
const MISSING_BG = 'color-mix(in srgb, var(--danger) 12%, transparent)';

function IconPlusSm() {
  return <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
    <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>;
}

// The editable grid, shared by the Receive form and the import preview.
// `withHeader` adds the consignment columns (Company / Their Ref / Received
// Date / Return By) at the front — the import preview only.
function MemoLinesGrid({
  rows, type, withHeader, showAllMissing, entities, settings, money, onUpdate, onRemove, onAdd, maxHeight,
}: {
  rows: MemoRow[]; type: MemoRowType; withHeader: boolean; showAllMissing: boolean;
  entities: Entity[]; settings: BusinessSettings | null; money: (v: number) => string;
  onUpdate: (key: string, field: MemoField, value: string) => void;
  onRemove: (key: string) => void;
  onAdd?: () => void;
  maxHeight: string;
}) {
  const cols = memoCols(type, withHeader);
  const inputCols = cols.filter(c => !c.formula && c.field !== 'category');
  const nav = useGridKeyNav(rows.length, inputCols.length);
  const lists = useMemo(() => memoLists(settings), [settings]);
  const companies = useMemo(() => [...entities].sort((a, b) => a.companyName.localeCompare(b.companyName)), [entities]);
  const tabCt  = rows.filter(memoRowStarted).reduce((s, r) => s + num(r.carat), 0);
  const tabAmt = rows.filter(memoRowStarted).reduce((s, r) => s + memoRowCost(r), 0);
  const foot: CSSProperties = { ...GTD_TEXT, position: 'sticky', bottom: 0, background: 'var(--surface-2)', fontFamily: 'var(--font-num)', fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap', color: 'var(--text-1)' };

  function cell(r: MemoRow, c: MemoCol, idx: number) {
    if (c.formula) {
      const v = memoRowCost(r);
      return <td key={c.field} style={{ ...GTD_TEXT, fontFamily: 'var(--font-num)', textAlign: 'right', color: 'var(--text-2)', background: 'var(--surface-2)', whiteSpace: 'nowrap' }}>
        {v > 0 ? money(v) : <span style={{ opacity: 0.4 }}>—</span>}
      </td>;
    }
    if (c.field === 'category') {
      return <td key={c.field} style={GTD_TEXT}>
        <CategoryPicker value={r.category} onChange={v => onUpdate(r._key, 'category', v)} />
      </td>;
    }
    const colIdx = inputCols.indexOf(c);
    const value  = String(r[memoKey(c.field)!] ?? '');
    const red    = (showAllMissing || memoRowStarted(r)) && memoCellMissing(r, c, withHeader);
    const tdStyle: CSSProperties = { ...GTD, minWidth: c.width * 7, background: red ? MISSING_BG : undefined };
    const cellProps = { ref: nav.cellRef(idx, colIdx), onKeyDown: nav.onCellKeyDown(idx, colIdx) };
    const disabled = (c.field === 'certificateNo' || c.field === 'listPrice' || c.field === 'discount') && r.lab === 'None';

    // Loose Size / Sub Size / Color / Clarity: type freely or pick a saved
    // value (new ones are added to the list on save), like Purchase's grid.
    const typeAhead: string[] | null =
      c.field === 'size'    && type === 'loose' ? settings?.looseSizeOptions ?? []
      : c.field === 'subSize' && type === 'loose' ? settings?.looseSubSizeOptions ?? []
      : c.list === 'looseColor'   ? lists.looseColor
      : c.list === 'looseClarity' ? lists.looseClarity
      : null;
    if (typeAhead) {
      return <td key={c.field} style={tdStyle}>
        <OptionCombobox cellRef={nav.cellRef(idx, colIdx)} onKeyDown={nav.onCellKeyDown(idx, colIdx)}
          className="grid-cell" placeholder={c.placeholder ?? c.header} options={typeAhead}
          panelMinWidth={110} value={value} onChange={v => onUpdate(r._key, c.field, v)} />
      </td>;
    }
    if (c.list) {
      const opts = c.list === 'company'
        ? companies.map(e => ({ value: e.id, label: e.companyName }))
        : lists[c.list].map(o => ({ value: o, label: o }));
      // A non-standard value from an imported file stays selectable instead of silently vanishing.
      if (value && !opts.some(o => o.value === value)) opts.push({ value, label: `${value} (from file)` });
      return <td key={c.field} style={tdStyle}>
        <select {...cellProps} className="grid-cell grid-cell-select" value={value} onChange={e => onUpdate(r._key, c.field, e.target.value)}>
          <option value="">{c.list === 'company' ? 'Select company…' : c.header}</option>
          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </td>;
    }
    return <td key={c.field} style={tdStyle}>
      <input {...cellProps}
        className={`grid-cell${c.numeric ? ' no-spinner' : ''}`}
        type={c.date ? 'date' : c.numeric ? 'number' : 'text'} step="any"
        style={c.numeric ? { textAlign: 'right', fontFamily: 'var(--font-num)' } : c.field === 'sku' || c.field === 'certificateNo' || c.field === 'supplierRef' ? { fontFamily: 'var(--font-num)' } : undefined}
        placeholder={disabled ? 'n/a' : c.placeholder ?? ''}
        value={value} disabled={disabled}
        onChange={e => onUpdate(r._key, c.field, e.target.value)}
      />
    </td>;
  }

  return (
    <div style={{
      border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface)',
      overflow: 'auto', maxHeight, minHeight: 200, overscrollBehavior: 'contain',
    }}>
      <table className="excel-grid-table">
        <thead>
          <tr>
            <th style={GTH}>#</th>
            {cols.map(c => (
              <th key={c.field} style={{ ...GTH, minWidth: c.field === 'category' ? undefined : c.width * 7, textAlign: c.numeric ? 'right' : 'left' }}>
                {c.field === 'category' ? 'Cat.' : c.header}
                {(c.required && (!c.headerCol || withHeader)) || c.field === 'certificateNo' ? <span style={{ color: 'var(--danger)' }}> *</span> : null}
              </th>
            ))}
            <th className="excel-grid-remove-col" style={GTH} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={r._key}>
              <td style={GTD_TEXT}>{idx + 1}</td>
              {cols.map(c => cell(r, c, idx))}
              <td className="excel-grid-remove-col" style={{ ...GTD_TEXT, textAlign: 'center' }}>
                <button type="button" className="line-item-remove" title="Remove row" onClick={() => onRemove(r._key)}>
                  <IconTrash />
                </button>
              </td>
            </tr>
          ))}
          {onAdd ? (
            <tr>
              <td colSpan={cols.length + 2} style={{ padding: 0, background: 'var(--primary-light)' }}>
                <button type="button" onClick={() => { const i = rows.length; onAdd(); setTimeout(() => nav.focusCell(i, 0), 0); }} style={{
                  display: 'flex', alignItems: 'center', gap: 6, width: '100%', height: 36, padding: '0 12px',
                  fontSize: 12, fontWeight: 700, color: 'var(--primary)',
                  background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                }}>
                  <IconPlusSm /> Add {type === 'loose' ? 'Loose' : 'Certified'} Row
                </button>
              </td>
            </tr>
          ) : rows.length === 0 && (
            <tr>
              <td colSpan={cols.length + 2} style={{ ...GTD_TEXT, textAlign: 'center', color: 'var(--text-3)', padding: '28px 8px' }}>
                No {type === 'loose' ? 'loose' : 'certified'} stones.
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td style={{ ...foot, textAlign: 'left' }}>Σ</td>
            {cols.map(c => (
              <td key={c.field} style={foot}>
                {c.field === 'carat' ? tabCt.toFixed(2) : c.field === 'totalAmount' ? money(tabAmt) : ''}
              </td>
            ))}
            <td className="excel-grid-remove-col" style={foot} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Same rules as the form's own handlers: certified pricing stays in sync.
function applyMemoUpdate(r: MemoRow, field: MemoField, value: string): MemoRow {
  if (field === 'listPrice') return resolveMemoPrice({ ...r, listPrice: value });
  if (field === 'discount')  return resolveMemoPrice({ ...r, discount: value, priceMode: 'discount' });
  if (field === 'pricePerCt' && r.itemType === 'certified') return resolveMemoPrice({ ...r, pricePerCt: value, priceMode: 'price' });
  const k = memoKey(field);
  return k ? { ...r, [k]: value } : r;
}

function memoToFixCount(rows: MemoRow[], withHeader: boolean, showAll: boolean): number {
  return rows.reduce((n, r) => (showAll || memoRowStarted(r)
    ? n + memoCols(r.itemType, withHeader).filter(c => !c.formula && memoCellMissing(r, c, withHeader)).length
    : n), 0);
}

interface MemoHeader { fromEntityId: string; supplierRef: string; date: string; returnBy: string; notes: string }

function validateMemo(h: MemoHeader, rows: MemoRow[]): string {
  if (!h.fromEntityId) return 'Choose the supplier.';
  if (!h.date) return 'Set the received date.';
  if (rows.length === 0) return 'Add at least one stone.';
  for (const r of rows) {
    const miss = memoCols(r.itemType, false).find(c => !c.formula && memoCellMissing(r, c, false));
    if (miss) {
      const n = rows.filter(x => x.itemType === r.itemType).indexOf(r) + 1;
      return `${r.itemType === 'loose' ? 'Loose' : 'Certified'} row ${n}: ${miss.header} is required.`;
    }
  }
  return '';
}

// The receive transaction — shared by the Receive form and the Excel import
// (one call per consignment), so both write identical docs.
async function createSupplierMemo(opts: {
  db: Firestore; businessId: string; userId: string; settings: BusinessSettings;
  header: MemoHeader; rows: MemoRow[];
}): Promise<string> {
  const { db, businessId, userId, settings, header, rows } = opts;
  const prefix = (settings.memoInPrefix || 'MI').toUpperCase();
  const memoRef = doc(collection(db, 'supplierMemos'));
  const counterRef = doc(db, 'counters', businessId);
  const totalCarat = round2(rows.reduce((s, r) => s + num(r.carat), 0));
  const totalCost  = round2(rows.reduce((s, r) => s + memoRowCost(r), 0));
  let memoNo = '';

  await runTransaction(db, async tx => {
    const cs = await tx.get(counterRef);
    const memoN = (cs.exists() ? (cs.data().supplierMemo ?? 0) : 0) + 1;
    memoNo = `${prefix}-${new Date().getFullYear()}-${String(memoN).padStart(4, '0')}`;
    tx.set(counterRef, stripUndef({ supplierMemo: memoN }), { merge: true });

    const opt = (v: string) => v.trim() || undefined;
    const lines: SupplierMemoLineItem[] = rows.map(r => {
      const isCert = r.itemType === 'certified';
      const hasList = isCert && num(r.listPrice) > 0;
      return stripUndef({
        id: crypto.randomUUID(), itemType: r.itemType,
        category: r.category || undefined,
        shape: r.shape.trim(), carat: round2(num(r.carat)),
        color: r.color.trim(), clarity: r.clarity.trim(),
        description:   opt(r.description),
        sku:           !isCert ? opt(r.sku) : undefined,
        size:          !isCert ? opt(r.size) : undefined,
        subSize:       !isCert ? opt(r.subSize) : undefined,
        lab:           isCert ? opt(r.lab) : undefined,
        certificateNo: isCert ? opt(r.certificateNo) : undefined,
        cut:           isCert ? opt(r.cut) : undefined,
        polish:        isCert ? opt(r.polish) : undefined,
        symmetry:      isCert ? opt(r.symmetry) : undefined,
        fluorescence:  isCert ? opt(r.fluorescence) : undefined,
        measLength:    isCert ? opt(r.measLength) : undefined,
        measWidth:     isCert ? opt(r.measWidth) : undefined,
        measDepth:     isCert ? opt(r.measDepth) : undefined,
        priceListAmount:  hasList ? num(r.listPrice) : undefined,
        priceListPercent: hasList ? num(r.discount) : undefined,
        costUSD: memoRowCost(r), status: 'held' as const,
      }) as SupplierMemoLineItem;
    });

    tx.set(memoRef, stripUndef({
      businessId, memoNo,
      fromEntityId: header.fromEntityId, supplierRef: header.supplierRef.trim() || undefined,
      date: header.date, returnByDate: header.returnBy || undefined,
      lines, totalCarat, totalCostUSD: totalCost, heldCostUSD: totalCost,
      status: 'open' as const,
      notes: header.notes.trim() || undefined,
      createdAt: serverTimestamp(), createdBy: userId,
    }));

    postEntry(tx, db, buildEntry({
      businessId, sourceType: 'memo_in', sourceId: memoRef.id,
      sourceCollection: 'supplierMemos', date: header.date, currency: settings.currency, createdBy: userId,
      note: 'Consignment received',
      lines: linesMemoIn(totalCost),
    }), entryId('memo_in', memoRef.id));
  });
  // New Size / Sub Size / Color / Clarity typed on loose lines join their
  // saved lists (same as Purchase). The memo is already saved, so a failure is ignored.
  const loose = rows.filter(r => r.itemType === 'loose');
  const lists = memoLists(settings);
  rememberLooseOptions(db, businessId, [
    { field: 'looseSizeOptions',    offered: settings.looseSizeOptions ?? [],    stored: settings.looseSizeOptions,    values: loose.map(r => r.size) },
    { field: 'looseSubSizeOptions', offered: settings.looseSubSizeOptions ?? [], stored: settings.looseSubSizeOptions, values: loose.map(r => r.subSize) },
    { field: 'looseColorOptions',   offered: lists.looseColor,   stored: settings.looseColorOptions,   values: loose.map(r => r.color) },
    { field: 'looseClarityOptions', offered: lists.looseClarity, stored: settings.looseClarityOptions, values: loose.map(r => r.clarity) },
  ]).catch(() => {});
  return memoNo;
}

function ReceiveModal({ entities, onClose, initialRows, initialHeader, importedFrom }: {
  entities: Entity[]; onClose: () => void;
  /** Rows from an Excel import (one consignment) — the form opens pre-filled. */
  initialRows?: MemoRow[];
  initialHeader?: Partial<MemoHeader>;
  importedFrom?: string;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [header, setHeader] = useState<MemoHeader>(() => ({
    fromEntityId: '', supplierRef: '', date: todayISO(), returnBy: '', notes: '',
    ...initialHeader,
  }));
  const [rows, setRows] = useState<MemoRow[]>(() => initialRows?.length ? initialRows : [newMemoRow('loose')]);
  const [tab, setTab] = useState<MemoRowType>(() => (initialRows?.length && !initialRows.some(r => r.itemType === 'loose') ? 'certified' : 'loose'));
  const [showAllMissing, setShowAllMissing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Snapshot at open — closing with anything different asks first, same
  // Discard / keep editing prompt as the Purchase and Sale forms. An
  // imported consignment counts as unsaved from the start.
  const [baseline] = useState(() => (initialRows?.length ? '' : JSON.stringify({ header, rows })));
  const setH = <K extends keyof MemoHeader>(k: K, v: MemoHeader[K]) => setHeader(h => ({ ...h, [k]: v }));

  const started = rows.filter(memoRowStarted);
  const totalCarat = round2(started.reduce((s, r) => s + num(r.carat), 0));
  const totalCost  = round2(started.reduce((s, r) => s + memoRowCost(r), 0));
  const looseRows = rows.filter(r => r.itemType === 'loose');
  const certRows  = rows.filter(r => r.itemType === 'certified');
  const looseFix  = memoToFixCount(looseRows, false, showAllMissing);
  const certFix   = memoToFixCount(certRows, false, showAllMissing);

  function requestClose() {
    if (JSON.stringify({ header, rows }) !== baseline) setConfirmDiscard(true);
    else onClose();
  }

  async function save() {
    const err = validateMemo(header, started);
    if (err) { setError(err); setShowAllMissing(true); return; }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }
    try {
      await createSupplierMemo({ db, businessId: business.id, userId: user!.id, settings, header, rows: started });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the consignment.');
    }
    setSaving(false);
  }

  const supplierName = entities.find(e => e.id === header.fromEntityId)?.companyName;

  return (
    <>
    <Modal open onClose={requestClose} size="full" hideHead
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ marginRight: 'auto' }}>
              <TotalsFigures figures={[
                { label: 'Stones', value: String(started.length) },
                { label: 'Carat', value: `${totalCarat.toFixed(2)} ct` },
                { label: 'Total Price', value: money(totalCost), strong: true },
              ]} />
            </div>
            <Button variant="secondary" onClick={requestClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={saving}>
              Receive Consignment ({started.length} stone{started.length === 1 ? '' : 's'})
            </Button>
          </div>
        </div>
      }>
      <ModalBanner
        title={supplierName || 'Receive Consignment'}
        eyebrow={importedFrom ? `Imported · ${importedFrom}` : 'Receive Consignment'}
        onClose={requestClose}
      />

      {/* ── Consignment header — same layout as the Add Purchase Invoice header ── */}
      <div className="form-section-label" style={{ marginBottom: 8 }}>Consignment Header</div>
      <div style={{ border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'hidden', marginBottom: 20 }}>
        <div className="form-table-compact" style={{ padding: '12px 14px 4px' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1.3 1 220px', minWidth: 200 }}>
              <FormField label="Company Name" required>
                <EntityCombobox
                  entities={entities.map(e => ({ id: e.id, companyName: e.companyName }))}
                  value={header.fromEntityId} onChange={v => setH('fromEntityId', v)} placeholder="Select supplier…"
                />
              </FormField>
            </div>
            <div style={{ width: 140, flexShrink: 0 }}>
              <FormField label="Their Memo / Ref No.">
                <input value={header.supplierRef} onChange={e => setH('supplierRef', e.target.value)}
                  placeholder="Their ref…" style={{ fontFamily: 'var(--font-num)' }} />
              </FormField>
            </div>
            <div style={{ width: 140, flexShrink: 0 }}>
              <FormField label="Received Date" required>
                <input type="date" value={header.date} max={todayISO()} onChange={e => setH('date', e.target.value)} />
              </FormField>
            </div>
            <div style={{ width: 150, flexShrink: 0 }}>
              <FormField label="Return By (optional)">
                <input type="date" value={header.returnBy} onChange={e => setH('returnBy', e.target.value)} />
                {header.returnBy && (
                  <p className="form-hint" style={{ color: 'var(--primary)', fontWeight: 600, marginTop: 3 }}>
                    {formatReturnBy(header.returnBy)}
                  </p>
                )}
              </FormField>
            </div>
            <div style={{ flex: '1 1 160px', minWidth: 140 }}>
              <FormField label="Notes (optional)">
                <input value={header.notes} onChange={e => setH('notes', e.target.value)} placeholder="Any notes…" />
              </FormField>
            </div>
          </div>
        </div>
      </div>

      {/* ── Loose / Certified — tab-switched Excel-style grids, same as Add Purchase Invoice ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <PageTabs
          className="purchase-items-tabs"
          tabs={[
            { key: 'loose',     label: `Loose Polished Line Items (${looseRows.filter(memoRowStarted).length})${looseFix ? ` · ${looseFix} to fix` : ''}` },
            { key: 'certified', label: `Certified Polished Line Items (${certRows.filter(memoRowStarted).length})${certFix ? ` · ${certFix} to fix` : ''}` },
          ]}
          active={tab}
          onChange={k => setTab(k as MemoRowType)}
        />
        <p className="form-hint" style={{ margin: 0 }}>Not in Inventory until converted to a purchase · memo number assigned on save</p>
      </div>

      <MemoLinesGrid
        rows={tab === 'loose' ? looseRows : certRows} type={tab} withHeader={false} showAllMissing={showAllMissing}
        entities={entities} settings={settings} money={money}
        onUpdate={(key, field, value) => setRows(rs => rs.map(r => (r._key === key ? applyMemoUpdate(r, field, value) : r)))}
        onRemove={key => setRows(rs => rs.filter(r => r._key !== key))}
        onAdd={() => setRows(rs => [...rs, newMemoRow(tab)])}
        maxHeight="calc(90vh - 430px)"
      />
    </Modal>

    <ConfirmModal
      open={confirmDiscard}
      title="Discard changes?"
      message="This consignment has unsaved changes. Discard them and close, or keep editing?"
      confirmLabel="Discard"
      onConfirm={() => { setConfirmDiscard(false); onClose(); }}
      onClose={() => setConfirmDiscard(false)}
    />
    </>
  );
}

// ── Excel import ─────────────────────────────────────────────────────────────
const MEMO_LOOSE_SHEET = 'Loose Polished';
const MEMO_CERT_SHEET  = 'Certified Polished';

async function downloadMemoInSample(settings: BusinessSettings | null, companyNames: string[]) {
  const supA = companyNames[0] ?? 'Your Supplier Name';
  const supB = companyNames[1] ?? companyNames[0] ?? 'Another Supplier';
  const cA = { company: supA, supplierRef: 'MEMO-501', date: xlsxUtcDate(addDaysToDate(todayISO(), -10)), returnBy: xlsxUtcDate(addDaysToDate(todayISO(), 20)) };
  const cB = { company: supB, supplierRef: 'M-77',     date: xlsxUtcDate(addDaysToDate(todayISO(), -3)) };
  type S = Partial<Record<MemoField, string | number | Date>>;
  const loose: S[] = [
    { ...cA, category: 'By Books & Real', sku: 'PARCEL-A', description: 'Melee parcel', shape: 'Round', size: '1.00-1.20', subSize: '+2', carat: 5.2, pricePerCt: 850, color: 'H', clarity: 'SI1' },
    { ...cA, category: 'By Books & Real', sku: 'PARCEL-B', shape: 'Oval', size: '0.80-0.90', carat: 3.1, pricePerCt: 620, color: 'I', clarity: 'SI2' },
  ];
  const cert: S[] = [
    { ...cB, category: 'By Books & Real', lab: 'GIA', certificateNo: '1234567890', shape: 'Round', carat: 1.01, listPrice: 12000, discount: -35, color: 'F', clarity: 'VS1', cut: 'Excellent', polish: 'Excellent', symmetry: 'Excellent', fluorescence: 'None', measLength: 6.45, measWidth: 6.48, measDepth: 3.99 },
    { ...cB, category: 'By Books & Real', lab: 'None', shape: 'Princess', carat: 0.9, pricePerCt: 5200, color: 'G', clarity: 'VS2' },
  ];
  await downloadImportWorkbook<MemoField>({
    fileName: 'receive-consignment-import-sample.xlsx',
    lists: memoLists(settings, companyNames),
    freeLists: ['company'],
    sheets: [
      { name: MEMO_LOOSE_SHEET, cols: memoCols('loose', true),     sample: loose, tabArgb: 'FF10B981' },
      { name: MEMO_CERT_SHEET,  cols: memoCols('certified', true), sample: cert,  tabArgb: 'FF4F46E5' },
    ],
    readme: [
      'Receive Consignment (Memo In) import — many consignments in one file',
      '',
      `• Put loose (parcel) goods on the "${MEMO_LOOSE_SHEET}" sheet and certified stones on the "${MEMO_CERT_SHEET}" sheet. Leave a sheet empty if you have none of that kind.`,
      '• Each row says which consignment it belongs to (the teal columns): Company Name, Their Ref, Received Date, Return By.',
      '• Rows with the same Company + Their Ref + Received Date become ONE consignment — even across the two sheets.',
      '• Company names must match a company already saved in DiaWolt (pick from the dropdown).',
      '• Only importing one consignment? You can leave all teal columns empty and fill the supplier and dates on the Receive Consignment screen instead.',
      '• Replace the example rows on each sheet with your own (delete them if not needed).',
      '• Row 1 headers must stay as they are. Column order can change, and optional columns can be deleted.',
      '• The grey Total Amount column is calculated for you (if you only have a total, put it there and leave Price/ct blank).',
      '• Price/ct is the supplier\'s price. It becomes your Cost/ct when you Convert to Purchase.',
      '• Certified Price/ct: give either Price/ct, or List Price/ct + Discount % (negative = discount, e.g. -35).',
      '• Consigned stones do not enter Inventory until you convert them to a purchase — you see and can fix every row in DiaWolt before anything is saved.',
    ],
  });
}

async function parseMemoInXlsx(data: ArrayBuffer, settings: BusinessSettings | null, entities: Entity[]):
  Promise<{ rows: MemoRow[]; warnings: string[]; hasHeaderInfo: boolean }> {
  const wb = await loadWorkbook(data);
  const lists = memoLists(settings);
  const warnings: string[] = [];
  const rows: MemoRow[] = [];
  let hasHeaderInfo = false;

  const read = (sheetName: string, type: MemoRowType, match: (n: string) => boolean) => {
    for (const { sheet, rowNo, text, raw } of readSheetRows(wb, sheetName, match, memoCols(type, true), warnings) ?? []) {
      const where = `${sheet} row ${rowNo}`;
      const get = (f: MemoField) => text[f] ?? '';
      const n   = (f: MemoField) => cleanNum(get(f));
      const opt = (f: MemoField, options: readonly string[], label: string) => matchOption(get(f), options, label, where, warnings);
      const date = (f: MemoField, label: string) => {
        const v = raw[f];
        if (v == null || v === '') return '';
        const d = parseImportDate(v);
        if (!d) warnings.push(`${where}: ${label} "${String(v)}" couldn't be read — set it in the preview.`);
        return d;
      };
      const isCert = type === 'certified';
      const r = newMemoRow(type);

      const company = get('company');
      if (company || get('supplierRef') || (raw.date != null && raw.date !== '')) hasHeaderInfo = true;
      if (company) {
        const hit = entities.find(e => e.companyName.trim().toLowerCase() === company.toLowerCase());
        if (!hit) warnings.push(`${where}: Company "${company}" not found in DiaWolt — pick it in the preview (or add it under Entities first).`);
        r.fromEntityId = hit?.id ?? '';
      }
      r.supplierRef = get('supplierRef');
      r.date        = date('date', 'Received Date');
      r.returnBy    = date('returnBy', 'Return By');

      const cat = get('category').toLowerCase();
      if (cat) {
        const hit = CATEGORY_ORDER.find(c => c === cat || CATEGORY_META[c].label.toLowerCase() === cat || CATEGORY_META[c].short.toLowerCase() === cat);
        if (hit) r.category = hit;
        else warnings.push(`${where}: Category "${get('category')}" not recognised — set to By Books & Real.`);
      }
      r.description = get('description');
      r.shape       = opt('shape', SHAPES, 'Shape');
      r.carat       = n('carat');
      r.color       = opt('color',   isCert ? lists.certColor   : lists.looseColor,   'Color');
      r.clarity     = opt('clarity', isCert ? lists.certClarity : lists.looseClarity, 'Clarity');
      const ct = num(r.carat), total = n('totalAmount');
      r.pricePerCt = n('pricePerCt') || (total && ct > 0 ? String(+(num(total) / ct).toFixed(4)) : '');

      if (isCert) {
        r.lab           = opt('lab', LABS, 'Lab');
        r.certificateNo = get('certificateNo');
        r.cut           = opt('cut', CUT_GRADES, 'Cut');
        r.polish        = opt('polish', CUT_GRADES, 'Polish');
        r.symmetry      = opt('symmetry', CUT_GRADES, 'Symmetry');
        r.fluorescence  = opt('fluorescence', FLUORESCENCE_GRADES, 'Fluorescence');
        r.measLength    = n('measLength');
        r.measWidth     = n('measWidth');
        r.measDepth     = n('measDepth');
        r.listPrice     = n('listPrice');
        r.discount      = n('discount');
        r.priceMode     = num(r.listPrice) > 0 && r.discount !== '' ? 'discount' : (r.pricePerCt ? 'price' : undefined);
        rows.push(resolveMemoPrice(r));
      } else {
        r.size    = get('size');
        r.subSize = get('subSize');
        r.sku     = get('sku');
        rows.push(r);
      }
    }
  };
  read(MEMO_LOOSE_SHEET, 'loose', n => n.includes('loose'));
  read(MEMO_CERT_SHEET, 'certified', n => n.includes('cert'));
  if (!wb.worksheets.some(w => /loose|cert/i.test(w.name))) {
    warnings.push(`No "${MEMO_LOOSE_SHEET}" or "${MEMO_CERT_SHEET}" sheet found — start from the sample file.`);
  }
  return { rows, warnings, hasHeaderInfo };
}

interface MemoGroup { key: string; header: MemoHeader; rows: MemoRow[]; incomplete: boolean }

// One consignment per Company + Their Ref + Received Date; Return By = the
// first row in the group that states one. Oldest first (memo numbers in date order).
function groupMemoRows(rows: MemoRow[], fileName: string): MemoGroup[] {
  const map = new Map<string, MemoGroup>();
  for (const r of rows) {
    const key = `${r.fromEntityId}|${r.supplierRef.trim().toLowerCase()}|${r.date}`;
    let g = map.get(key);
    if (!g) {
      g = { key, rows: [], incomplete: !r.fromEntityId || !r.date,
        header: { fromEntityId: r.fromEntityId, supplierRef: r.supplierRef.trim(), date: r.date, returnBy: '', notes: `Imported from ${fileName}` } };
      map.set(key, g);
    }
    if (!g.header.returnBy && r.returnBy) g.header.returnBy = r.returnBy;
    g.rows.push(r);
  }
  return [...map.values()].sort((a, b) => (a.header.date || '9999').localeCompare(b.header.date || '9999'));
}

function IconUploadFile() {
  return <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M12 17v-6" /><path d="M9.5 13.5 12 11l2.5 2.5" />
  </svg>;
}

// Pick the file → full-width editable preview: a Consignments summary on top
// (one row per Company + Their Ref + Received Date) and the same tabs + grid
// as the Receive form. "Receive N Consignments" saves them all; a file with
// no consignment columns filled opens the Receive form pre-filled instead.
function ImportMemoInModal({ entities, onClose, onReview, onImported }: {
  entities: Entity[]; onClose: () => void;
  onReview: (rows: MemoRow[], fileName: string, header?: Partial<MemoHeader>) => void;
  onImported: (memoNos: string[], done: boolean) => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const nameOf = (id: string) => entities.find(e => e.id === id)?.companyName ?? '';
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<MemoRow[]>([]);
  const [fileHasHeader, setFileHasHeader] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [tab, setTab] = useState<MemoRowType>('loose');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function readFile(f: File) {
    setError('');
    if (!/\.xlsx$/i.test(f.name)) { setError('Please choose an Excel (.xlsx) file — start from the sample file.'); return; }
    setBusy(true);
    try {
      const res = await parseMemoInXlsx(await f.arrayBuffer(), settings, entities);
      setFileName(f.name); setRows(res.rows); setWarnings(res.warnings); setFileHasHeader(res.hasHeaderInfo);
      setTab(res.rows.some(r => r.itemType === 'loose') || res.rows.length === 0 ? 'loose' : 'certified');
      if (res.rows.length === 0) setError('No stones found in that file.');
    } catch {
      setError('Could not read that file. Make sure it is an .xlsx saved from Excel, Numbers or Google Sheets.');
    }
    setBusy(false);
  }
  async function downloadSample() {
    try {
      await downloadMemoInSample(settings,
        entities.filter(e => e.types.includes('supplier')).map(e => e.companyName).sort((a, b) => a.localeCompare(b)));
    } catch { setError('Could not create the sample file.'); }
  }
  function requestClose() {
    if (saving) return;
    if (rows.length > 0) setConfirmDiscard(true); else onClose();
  }

  const multi  = fileHasHeader || rows.some(r => r.fromEntityId || r.date || r.supplierRef.trim());
  const groups = useMemo(() => (multi ? groupMemoRows(rows, fileName) : []), [multi, rows, fileName]);
  const looseRows = rows.filter(r => r.itemType === 'loose');
  const certRows  = rows.filter(r => r.itemType === 'certified');
  const looseFix  = memoToFixCount(looseRows, multi, true);
  const certFix   = memoToFixCount(certRows, multi, true);
  const totalCt   = rows.reduce((s, r) => s + num(r.carat), 0);
  const totalAmt  = rows.reduce((s, r) => s + memoRowCost(r), 0);

  async function importAll() {
    if (looseFix + certFix > 0) { setError(`Fill the ${looseFix + certFix} red cell${looseFix + certFix === 1 ? '' : 's'} first.`); return; }
    for (let i = 0; i < groups.length; i++) {
      const v = validateMemo(groups[i].header, groups[i].rows);
      if (v) { setError(`Consignment ${i + 1} (${nameOf(groups[i].header.fromEntityId)}): ${v}`); return; }
    }
    const db = getClientDb();
    if (!db || !business || !user) { setError('Not connected.'); return; }
    setError('');
    const nos: string[] = [];
    const saved = new Set<string>();
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      setSaving(`Saving ${i + 1} of ${groups.length}…`);
      try {
        nos.push(await createSupplierMemo({ db, businessId: business.id, userId: user.id, settings, header: g.header, rows: g.rows }));
        g.rows.forEach(r => saved.add(r._key));
      } catch (e) {
        // Stop; already-saved consignments leave the list so a retry can't duplicate them.
        setRows(prev => prev.filter(r => !saved.has(r._key)));
        setSaving('');
        setError(`${nos.length ? `Saved ${nos.length} of ${groups.length} (${nos.join(', ')}) — removed from this list. ` : ''}`
          + `Consignment ${i + 1} (${nameOf(g.header.fromEntityId)}) failed: ${e instanceof Error ? e.message : 'unknown error'}`);
        if (nos.length) onImported(nos, false);
        return;
      }
    }
    setSaving('');
    onImported(nos, true);
  }

  const invTh: CSSProperties = { ...GTH, whiteSpace: 'nowrap' };
  const invTd: CSSProperties = { ...GTD_TEXT, whiteSpace: 'nowrap', color: 'var(--text-2)' };
  const invNum: CSSProperties = { ...invTd, fontFamily: 'var(--font-num)', textAlign: 'right' };
  const dash = <span style={{ opacity: 0.4 }}>—</span>;

  return (
    <>
    <Modal open onClose={requestClose}
      title="Import Consignments"
      subtitle={fileName
        ? `${fileName} — check and fix the rows below${multi ? `, then receive ${groups.length} consignment${groups.length === 1 ? '' : 's'}` : ', then review the consignment'}`
        : 'Excel file with a Loose Polished sheet and a Certified Polished sheet'}
      size={rows.length > 0 ? 'full' : 'md'}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
          {rows.length > 0 && (
            <div style={{ marginRight: 'auto' }}>
              <TotalsFigures figures={[
                ...(multi ? [{ label: 'Consignments', value: String(groups.length) }] : []),
                { label: 'Stones', value: String(rows.length) },
                { label: 'Carat', value: `${totalCt.toFixed(2)} ct` },
                { label: 'Total Price', value: money(totalAmt), strong: true },
              ]} />
            </div>
          )}
          <Button variant="secondary" onClick={requestClose} disabled={!!saving}>Cancel</Button>
          {multi ? (
            <>
              {groups.length === 1 && !groups[0].incomplete && (
                <Button variant="secondary" disabled={!!saving} onClick={() => onReview(groups[0].rows, fileName, groups[0].header)}>
                  Open in Receive Form
                </Button>
              )}
              <Button variant="primary" loading={!!saving} disabled={groups.length === 0} onClick={importAll}>
                {saving || `Receive ${groups.length} Consignment${groups.length === 1 ? '' : 's'}`}
              </Button>
            </>
          ) : (
            <Button variant="primary" disabled={rows.length === 0} onClick={() => onReview(rows, fileName)}>
              Review Consignment →
            </Button>
          )}
        </div>
      }
    >
      <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) readFile(f); }} />

      {rows.length === 0 ? (
        <>
          <div
            role="button" tabIndex={0}
            onClick={() => fileRef.current?.click()}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click(); } }}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) readFile(f); }}
            style={{
              border: `1.5px dashed ${dragOver ? 'var(--primary)' : 'var(--border)'}`,
              background: dragOver ? 'var(--primary-light)' : 'var(--surface-2)',
              borderRadius: 'var(--r-sm)', padding: '26px 16px', textAlign: 'center', cursor: 'pointer',
              color: 'var(--text-3)', transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            <IconUploadFile />
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', marginTop: 6 }}>{busy ? 'Reading…' : 'Choose or drop an Excel file'}</div>
            <div style={{ fontSize: 11.5, marginTop: 2 }}>.xlsx · one or many consignments</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="line-item-link-btn" onClick={downloadSample}>Download Sample File</button>
          </div>
        </>
      ) : (
        <>
          {multi && (
            <>
              <div className="form-section-label" style={{ marginBottom: 8 }}>Consignments ({groups.length})</div>
              <div style={{ border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'auto', maxHeight: 170, marginBottom: 16, overscrollBehavior: 'contain' }}>
                <table className="excel-grid-table">
                  <thead>
                    <tr>
                      <th style={invTh}>#</th>
                      <th style={{ ...invTh, minWidth: 180 }}>Company</th>
                      <th style={invTh}>Their Ref</th>
                      <th style={invTh}>Received</th>
                      <th style={invTh}>Return By</th>
                      <th style={invTh}>Loose</th>
                      <th style={invTh}>Certified</th>
                      <th style={invTh}>Carat</th>
                      <th style={invTh}>Total Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g, i) => (
                      <tr key={g.key}>
                        <td style={invTd}>{i + 1}</td>
                        <td style={{ ...invTd, background: !g.header.fromEntityId ? MISSING_BG : undefined, color: 'var(--text-1)', fontWeight: 600 }}>
                          {nameOf(g.header.fromEntityId) || <span style={{ color: 'var(--danger)' }}>Company missing</span>}
                        </td>
                        <td style={{ ...invTd, fontFamily: 'var(--font-num)' }}>{g.header.supplierRef || dash}</td>
                        <td style={{ ...invTd, background: !g.header.date ? MISSING_BG : undefined }}>
                          {g.header.date ? fmtDate(g.header.date) : <span style={{ color: 'var(--danger)' }}>Date missing</span>}
                        </td>
                        <td style={invTd}>{g.header.returnBy ? fmtDate(g.header.returnBy) : dash}</td>
                        <td style={invNum}>{g.rows.filter(r => r.itemType === 'loose').length || dash}</td>
                        <td style={invNum}>{g.rows.filter(r => r.itemType === 'certified').length || dash}</td>
                        <td style={invNum}>{g.rows.reduce((s, r) => s + num(r.carat), 0).toFixed(2)}</td>
                        <td style={invNum}>{money(g.rows.reduce((s, r) => s + memoRowCost(r), 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
            <PageTabs
              className="purchase-items-tabs"
              tabs={[
                { key: 'loose',     label: `Loose Polished Line Items (${looseRows.length})${looseFix ? ` · ${looseFix} to fix` : ''}` },
                { key: 'certified', label: `Certified Polished Line Items (${certRows.length})${certFix ? ` · ${certFix} to fix` : ''}` },
              ]}
              active={tab}
              onChange={k => setTab(k as MemoRowType)}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <button type="button" className="line-item-link-btn" onClick={() => fileRef.current?.click()} disabled={!!saving}>
                {busy ? 'Reading…' : 'Choose a different file'}
              </button>
              <button type="button" className="line-item-link-btn" onClick={downloadSample}>Download Sample File</button>
            </div>
          </div>

          <MemoLinesGrid
            rows={tab === 'loose' ? looseRows : certRows} type={tab} withHeader={multi} showAllMissing
            entities={entities} settings={settings} money={money}
            onUpdate={(key, field, value) => setRows(rs => rs.map(r => (r._key === key ? applyMemoUpdate(r, field, value) : r)))}
            onRemove={key => setRows(rs => rs.filter(r => r._key !== key))}
            maxHeight={multi ? 'calc(90vh - 500px)' : 'calc(90vh - 300px)'}
          />

          {warnings.length > 0 && (
            <div className="msg msg-info" style={{ marginTop: 12, fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 110, overflowY: 'auto' }}>
              {warnings.join('\n')}
            </div>
          )}
        </>
      )}

      {error && <div className="msg msg-error" style={{ marginTop: 12 }}>{error}</div>}
    </Modal>

    <ConfirmModal
      open={confirmDiscard}
      title="Discard import?"
      message="The imported rows (and any edits made here) will be lost. Discard them and close, or keep editing?"
      confirmLabel="Discard"
      onConfirm={() => { setConfirmDiscard(false); onClose(); }}
      onClose={() => setConfirmDiscard(false)}
    />
    </>
  );
}

// ── Convert-to-purchase modal ───────────────────────────────────────────
// ── Convert to Purchase — the full Add-Purchase-Invoice experience, single
// row. This is the first time a memo line becomes real, owned inventory —
// nothing this detailed was captured at the lightweight receive step, so
// every field Purchase's own line-item card has (grading, List
// Price/Discount/Price, Added Value%, a live-previewed Code) is filled in
// here, mirroring Purchase.tsx's card + resolveCertPrice/calcBatchCert /
// calcBatchLoose math for a single item instead of a batch. ─────────────────
interface ConvertRow {
  code: string; description: string; category: TransactionCategory | '';
  lab: string; certificateNo: string;
  shape: string; carat: string; color: string; clarity: string;
  size: string; subSize: string; sku: string;
  cut: string; polish: string; symmetry: string; fluorescence: string;
  measLength: string; measWidth: string; measDepth: string;
  listPriceAmount: string; priceListPercent: string;
  priceMode: 'discount' | 'price' | undefined;
  pricePerCt: string;
  addedValuePct: string;
}
function resolveConvertPrice(r: ConvertRow, isCert: boolean): ConvertRow {
  if (!isCert) return r;
  const listPerCt = num(r.listPriceAmount);
  if (r.priceMode === 'price') {
    const price = num(r.pricePerCt);
    if (listPerCt <= 0) return r;
    const discountPct = +(((price - listPerCt) / listPerCt) * 100).toFixed(4);
    return { ...r, priceListPercent: String(discountPct) };
  }
  const discountPct = num(r.priceListPercent);
  const price = listPerCt * (1 + discountPct / 100);
  return { ...r, pricePerCt: listPerCt > 0 ? String(+price.toFixed(4)) : r.pricePerCt };
}
function calcConvertRow(r: ConvertRow, carat: number) {
  const ppc = num(r.pricePerCt);
  const avPct = num(r.addedValuePct);
  const amount  = ppc * carat;
  const costAmt = amount * (1 + avPct / 100);
  return { amount, costAmt, costPerCt: carat > 0 ? costAmt / carat : 0 };
}

function ConvertModal({ memo, line, supplierName, entities, onClose }: {
  memo: SupplierMemo; line: SupplierMemoLineItem; supplierName: string; entities: Entity[]; onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const isCert = line.itemType === 'certified';
  const brokers = useMemo(() => entities.filter(e => e.types.includes('broker')), [entities]);

  const [supplierInvoiceNo, setSupplierInvoiceNo] = useState(memo.supplierRef ?? '');
  const [date, setDate] = useState(todayISO());
  const [terms, setTerms] = useState(30);
  const [notes, setNotes] = useState('');
  const [brokerEntityId, setBrokerEntityId] = useState('');
  const [brokerCommissionPercent, setBrokerCommissionPercent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dueDate = addDaysToDate(date, terms);

  const startPrice = line.carat > 0 ? round2((line.costUSD ?? 0) / line.carat) : 0;
  const [row, setRow] = useState<ConvertRow>({
    // Everything captured at receive time (the grid-style Receive form)
    // carries over; lines received before it just start blank as before.
    code: line.code ?? '', description: line.description ?? '', category: line.category ?? 'books-real',
    lab: line.lab ?? '', certificateNo: line.certificateNo ?? '',
    shape: line.shape, carat: String(line.carat), color: line.color, clarity: line.clarity,
    size: line.size ?? '', subSize: line.subSize ?? '', sku: line.sku ?? '',
    cut: line.cut ?? '', polish: line.polish ?? '', symmetry: line.symmetry ?? '', fluorescence: line.fluorescence ?? '',
    measLength: line.measLength ?? '', measWidth: line.measWidth ?? '', measDepth: line.measDepth ?? '',
    listPriceAmount: line.priceListAmount != null ? String(line.priceListAmount) : '',
    priceListPercent: line.priceListPercent != null ? String(line.priceListPercent) : '',
    priceMode: line.priceListAmount != null ? 'price' : undefined,
    pricePerCt: startPrice > 0 ? String(startPrice) : '',
    addedValuePct: '3',
  });
  const setR = (patch: Partial<ConvertRow>) => setRow(r => ({ ...r, ...patch }));
  const updateListPrice = (v: string) => setRow(r => resolveConvertPrice({ ...r, listPriceAmount: v }, isCert));
  const updateDiscount   = (v: string) => setRow(r => resolveConvertPrice({ ...r, priceListPercent: v, priceMode: 'discount' }, isCert));
  const updatePrice      = (v: string) => isCert
    ? setRow(r => resolveConvertPrice({ ...r, pricePerCt: v, priceMode: 'price' }, isCert))
    : setR({ pricePerCt: v });

  const looseColors    = settings.looseColorOptions?.length   ? settings.looseColorOptions   : COLORS;
  const looseClarities = settings.looseClarityOptions?.length ? settings.looseClarityOptions : CLARITIES;
  const colorList   = isCert ? COLORS : looseColors;
  const clarityList = isCert ? CLARITIES : looseClarities;

  // Live preview of the next auto Code — same idea as Purchase's
  // BatchAddModal; only a preview, the real guarantee is the itemCodes
  // collision check at save time.
  useEffect(() => {
    if (row.code || !business) return;
    const db = getClientDb();
    if (!db) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'counters', business.id));
        const base = snap.exists() ? (snap.data().itemCode ?? settings.itemCodeStart ?? 0) : (settings.itemCodeStart ?? 0);
        if (!cancelled) setR({ code: String(base + 1) });
      } catch { /* leave blank — still fine to type manually */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business]);

  const carat = num(row.carat);
  const calc = calcConvertRow(row, carat);

  function validate(): string {
    if (!row.shape.trim()) return 'Shape is required.';
    if (carat <= 0) return 'Carat must be greater than 0.';
    if (!row.color.trim() || !row.clarity.trim()) return 'Color and Clarity are required.';
    if (isCert) {
      if (!row.lab.trim()) return 'Lab is required.';
      if (row.lab !== 'None' && !row.certificateNo.trim()) return 'Certificate No is required.';
    } else if (!row.sku.trim()) {
      return 'SKU is required for a loose stone.';
    }
    if (num(row.pricePerCt) <= 0) return 'Cost/ct must be greater than 0.';
    return '';
  }

  async function run() {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }

    const prefix = isCert ? (settings.certifiedPrefix || 'PC') : (settings.loosePrefix || 'PL');
    const counterField = isCert ? 'polishedCertified' : 'polishedLoose';
    const invRef = doc(collection(db, 'invoices'));
    const memoRef = doc(db, 'supplierMemos', memo.id);
    const counterRef = doc(db, 'counters', business.id);
    // A legacy in-flight line (received before this model) already has a
    // real inventory doc from the old memo-in flow — reuse it instead of
    // minting a second, orphaned one. New-style lines create fresh.
    const legacyItemRef = line.inventoryItemId ? doc(db, 'inventory', line.inventoryItemId) : null;
    const newItemRef = legacyItemRef ?? doc(collection(db, 'inventory'));
    const typedCode = row.code.trim();
    const typedCodeId = typedCode ? normalizeCode(typedCode) : null;
    const typedCodeRef = typedCodeId ? doc(db, 'itemCodes', typedCodeId) : null;
    const skuId = !isCert && row.sku.trim() ? slugifySku(row.sku) : null;
    const skuRef = skuId ? doc(db, 'skus', skuId) : null;

    try {
      await runTransaction(db, async tx => {
        const [cs, ms, legacySnap, codeSnap, skuSnap] = await Promise.all([
          tx.get(counterRef), tx.get(memoRef),
          legacyItemRef ? tx.get(legacyItemRef) : Promise.resolve(null),
          typedCodeRef ? tx.get(typedCodeRef) : Promise.resolve(null),
          skuRef ? tx.get(skuRef) : Promise.resolve(null),
        ]);
        if (!ms.exists()) throw new Error('That memo no longer exists.');
        const m = ms.data() as SupplierMemo;
        const cur = m.lines.find(l => l.id === line.id);
        if (!cur || cur.status !== 'held') throw new Error('That stone is no longer held on this memo.');
        if (codeSnap && codeSnap.exists()) throw new Error(`Code "${typedCode}" is already used by another item.`);

        const lineCarat = round2(carat);
        const amount = round2(calc.amount);
        const costingAmount = round2(calc.costAmt);
        const storedCursor = cs.exists() ? (cs.data().itemCode ?? settings.itemCodeStart ?? 0) : (settings.itemCodeStart ?? 0);
        const brokerPct = brokerEntityId ? (num(brokerCommissionPercent) || 0) : undefined;
        const brokerUSD = brokerEntityId ? round2(amount * (brokerPct ?? 0) / 100) : undefined;

        let finalCode = typedCode;
        if (!finalCode) {
          const cursor = { n: storedCursor };
          finalCode = await nextFreeCode(tx, db, cursor, new Set());
        }
        const finalCodeId = normalizeCode(finalCode);

        const count = cs.exists() ? (cs.data()[counterField] ?? 0) : 0;
        const invoiceNo = fmtInvoiceNo(prefix, count + 1);
        const codeNum = Number(finalCode);
        const counterUpdate: Record<string, number> = { [counterField]: count + 1 };
        if (Number.isFinite(codeNum) && codeNum > storedCursor) counterUpdate.itemCode = codeNum;
        tx.set(counterRef, counterUpdate, { merge: true });

        const li: PurchaseLineItem = stripUndef({
          id: line.id, itemType: line.itemType,
          shape: row.shape.trim(), carat: lineCarat, color: row.color.trim(), clarity: row.clarity.trim(),
          lab: isCert ? (row.lab.trim() || undefined) : undefined,
          certificateNo: isCert ? (row.certificateNo.trim() || undefined) : undefined,
          rapPricePerCt: isCert ? (num(row.listPriceAmount) || undefined) : undefined,
          priceListPercent: isCert && row.priceListPercent ? num(row.priceListPercent) : undefined,
          priceListAmount: isCert ? (num(row.listPriceAmount) || undefined) : undefined,
          cut: isCert ? (row.cut || undefined) : undefined,
          polish: isCert ? (row.polish || undefined) : undefined,
          symmetry: isCert ? (row.symmetry || undefined) : undefined,
          fluorescence: isCert ? (row.fluorescence || undefined) : undefined,
          measLength: isCert ? (row.measLength || undefined) : undefined,
          measWidth: isCert ? (row.measWidth || undefined) : undefined,
          measDepth: isCert ? (row.measDepth || undefined) : undefined,
          size: !isCert ? (row.size.trim() || undefined) : undefined,
          subSize: !isCert ? (row.subSize.trim() || undefined) : undefined,
          sku: !isCert ? (row.sku.trim() || undefined) : undefined,
          code: finalCode, description: row.description.trim() || undefined,
          category: row.category || undefined,
          amountUSD: amount, addedValuePercent: num(row.addedValuePct),
          costingPercent: 0, costingAmountUSD: costingAmount,
          costPerCt: lineCarat > 0 ? round2(costingAmount / lineCarat) : 0,
        }) as PurchaseLineItem;

        tx.set(invRef, stripUndef({
          businessId: business.id,
          type: (isCert ? 'polished-certified' : 'polished-loose') as PurchaseType,
          invoiceNo, fromEntityId: m.fromEntityId,
          supplierInvoiceNo: supplierInvoiceNo.trim() || undefined,
          purchaseDate: date, terms, dueDate,
          itemCount: 1, totalCarat: lineCarat, totalAmountUSD: amount, totalCostingUSD: costingAmount,
          paidUSD: 0, paymentStatus: 'unpaid' as PaymentStatus,
          brokerEntityId: brokerEntityId || undefined,
          brokerCommissionPercent: brokerPct,
          brokerCommissionUSD: brokerUSD,
          brokerPaidUSD: brokerEntityId ? 0 : undefined,
          brokerPaymentStatus: brokerEntityId ? ('unpaid' as PaymentStatus) : undefined,
          lineItems: [li], notes: notes.trim() || `Converted from memo ${m.memoNo}`,
          createdAt: serverTimestamp(), updatedAt: serverTimestamp(), ...createdStamp(user),
        }));
        logActivity(tx, db, business.id, user, {
          module: 'purchase', action: 'create', recordId: invRef.id, recordNo: invoiceNo,
          partyId: m.fromEntityId, itemCount: 1, carat: lineCarat, amountUSD: amount,
          note: `From memo ${m.memoNo}`,
        });

        const invItemId = legacyItemRef ? legacyItemRef.id : newItemRef.id;
        const itemFields = {
          purchaseId: invRef.id, purchaseNo: invoiceNo, lineItemId: line.id,
          type: isCert ? 'polished-certified' : 'polished-loose',
          shape: li.shape, carat: li.carat, color: li.color, clarity: li.clarity,
          lab: li.lab, certificateNo: li.certificateNo,
          cut: li.cut, polish: li.polish, symmetry: li.symmetry, fluorescence: li.fluorescence,
          measLength: li.measLength, measWidth: li.measWidth, measDepth: li.measDepth,
          size: li.size, subSize: li.subSize,
          sku: li.sku, skuId: skuId ?? undefined,
          code: finalCode, codeId: finalCodeId, category: li.category,
          costUSD: amount, costingUSD: costingAmount,
          updatedAt: serverTimestamp(),
        };
        if (legacyItemRef && legacySnap?.exists()) {
          tx.update(legacyItemRef, stripUndef({
            ...itemFields, provenance: 'owned', supplierMemoId: null,
          }));
        } else {
          tx.set(newItemRef, stripUndef({
            ...itemFields, businessId: business.id, status: 'in-stock', createdAt: serverTimestamp(),
          }));
        }

        tx.set(doc(db, 'itemCodes', finalCodeId), stripUndef({
          businessId: business.id, code: finalCode, inventoryItemId: invItemId,
          createdAt: serverTimestamp(), createdBy: user!.id,
        }));
        tx.set(doc(collection(db, 'itemCodes', finalCodeId, 'history')), stripUndef({
          type: 'purchase' as CodeEventType, date,
          carat: lineCarat, entityId: m.fromEntityId,
          pricePerCt: lineCarat > 0 ? amount / lineCarat : 0,
          amountUSD: amount, inventoryItemId: invItemId,
          category: li.category,
          notes: `Lot ${invoiceNo}`,
          createdAt: serverTimestamp(), createdBy: user!.id,
        }));

        if (skuId && skuRef) {
          const skuExists = skuSnap?.exists() ?? false;
          const prevAvg = skuExists ? ((skuSnap!.data() as Sku).currentCostingPerCt ?? 0) : 0;
          if (!skuExists) {
            tx.set(skuRef, stripUndef({
              businessId: business.id, sku: row.sku.trim(),
              shape: row.shape.trim(), color: row.color.trim(), clarity: row.clarity.trim(),
              size: row.size.trim() || undefined, subSize: row.subSize.trim() || undefined,
              currentAvgCostPerCt: 0, currentAddedValuePercent: 0, currentCostingPerCt: 0,
              lastActivityAt: todayISO(), createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
            }));
          } else {
            tx.update(skuRef, { lastActivityAt: todayISO(), updatedAt: serverTimestamp() });
          }
          tx.set(doc(db, 'skus', skuId, 'history', line.id), stripUndef({
            type: 'purchase', date, supplierEntityId: m.fromEntityId,
            purchaseId: invRef.id, purchaseNo: invoiceNo, lineItemId: line.id,
            quantityCarat: lineCarat, unitCostPerCt: lineCarat > 0 ? amount / lineCarat : 0, totalCost: amount,
            inventoryItemId: invItemId,
            previousAvgCostPerCt: prevAvg, newAvgCostPerCt: prevAvg,
            createdAt: serverTimestamp(), createdBy: user!.id,
          }));
        }

        const newLines = m.lines.map(l => l.id === line.id
          ? { ...l, status: 'purchased' as const, purchaseInvoiceId: invRef.id, purchaseInvoiceNo: invoiceNo }
          : l);
        const held = round2(newLines.filter(l => l.status === 'held').reduce((s, l) => s + (l.costUSD ?? 0), 0));
        tx.update(memoRef, {
          lines: newLines, heldCostUSD: held,
          status: held > 0.005 ? 'open' : 'closed', updatedAt: serverTimestamp(),
        });

        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType: 'purchase_invoice', sourceId: invRef.id,
          sourceCollection: 'invoices', date, currency: settings.currency, createdBy: user!.id,
          note: `Purchase ${invoiceNo}`,
          lines: linesPurchaseInvoice(amount, m.fromEntityId),
        }), entryId('purchase_invoice', invRef.id));

        // Broker commission accrues as its own entry, settled independently
        // of the supplier's own payment — mirrors Purchase.tsx's own flow.
        if (brokerEntityId && (brokerUSD ?? 0) > 0.005) {
          postEntry(tx, db, buildEntry({
            businessId: business.id, sourceType: 'broker_accrual', sourceId: invRef.id,
            sourceCollection: 'invoices', date, currency: settings.currency, createdBy: user!.id,
            note: `Broker commission — ${invoiceNo}`,
            lines: linesBrokerAccrual(brokerUSD!, brokerEntityId),
          }), entryId('broker_accrual', invRef.id));
        }

        if (held > 0.005) {
          postEntry(tx, db, buildEntry({
            businessId: business.id, sourceType: 'memo_in', sourceId: memo.id,
            sourceCollection: 'supplierMemos', date: m.date, currency: settings.currency, createdBy: user!.id,
            note: `Memo in ${m.memoNo}`, lines: linesMemoIn(held),
          }), entryId('memo_in', memo.id));
        } else {
          tx.delete(doc(db, 'journalEntries', entryId('memo_in', memo.id)));
        }
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not convert to a purchase.');
    }
    setSaving(false);
  }

  return (
    <Modal open onClose={onClose} size="2xl" title="Convert to Purchase"
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={run} loading={saving}>Create Purchase</Button>
      </>}>
      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

      {/* ── Purchase header — same layout as Add Purchase Invoice's own header ── */}
      <div className="form-section-label" style={{ marginBottom: 8 }}>Purchase Header</div>
      <div style={{ border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'hidden', marginBottom: 20 }}>
        <div className="form-table-compact" style={{ padding: '12px 14px 4px' }}>
          <div className="form-row-3" style={{ marginBottom: 0 }}>
            <FormField label="Supplier">
              <input value={supplierName} disabled />
            </FormField>
            <FormField label="Purchase date" required>
              <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
            </FormField>
            <FormField label="Supplier invoice no (optional)">
              <input value={supplierInvoiceNo} onChange={e => setSupplierInvoiceNo(e.target.value)} />
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flexShrink: 0 }}>
              <FormField label="Payment Terms">
                <TermsSelector value={terms} onChange={setTerms} />
              </FormField>
              <p className="form-hint" style={{ marginTop: 3 }}>Due {fmtDate(dueDate)}</p>
            </div>
            <div style={{ width: 260, flexShrink: 0 }}>
              <FormField label="Broker (optional)">
                <div style={{ display: 'flex', gap: 6 }}>
                  <EntityCombobox
                    style={{ flex: 1, minWidth: 0 }}
                    entities={brokers}
                    value={brokerEntityId}
                    onChange={v => { setBrokerEntityId(v); if (!v) setBrokerCommissionPercent(''); }}
                    placeholder="No broker"
                    clearLabel="No broker"
                  />
                  {brokerEntityId && (
                    <Input
                      type="number" step="0.1" min="0" max="100"
                      className="no-spinner"
                      value={brokerCommissionPercent}
                      onChange={e => setBrokerCommissionPercent(e.target.value)}
                      placeholder="0" suffix="%"
                      style={{ width: 62, flexShrink: 0, textAlign: 'right' }}
                      title="Broker commission %"
                    />
                  )}
                </div>
                {brokerEntityId && num(brokerCommissionPercent) > 0 && (
                  <p className="form-hint" style={{ marginTop: 3 }}>
                    Commission ≈ {money(round2(calc.amount * num(brokerCommissionPercent) / 100))}
                  </p>
                )}
              </FormField>
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <FormField label="Notes (optional)">
                <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes…" />
              </FormField>
            </div>
          </div>
        </div>
      </div>

      {/* ── The stone — same detailed line-item card as Purchase's own Add Invoice ── */}
      <div className="form-section-label" style={{ marginBottom: 8 }}>Diamond Line Item</div>
      <div className="line-item-card">
        <div className="line-item-card-head">
          <span className="line-item-card-index">#1</span>
          <RowTypePill value={isCert ? 'certified' : 'loose'} onChange={() => {}} />
          <CategoryPicker value={row.category} onChange={v => setR({ category: v })} />
          <div className="line-item-card-stats">
            <label>
              Added Value
              <input className="line-item-card-stats-input" type="number" step="0.1" min="0" placeholder="3"
                value={row.addedValuePct} onChange={e => setR({ addedValuePct: e.target.value })} />
              %
            </label>
            <span title="Cost/ct plus Added Value %">Price/ct <strong>{calc.costPerCt > 0 ? money(calc.costPerCt) : '—'}</strong></span>
          </div>
        </div>

        <div className="line-item-grid">
          <LineField label="Code">
            <input style={TI} placeholder="Auto" value={row.code} onChange={e => setR({ code: e.target.value })} />
          </LineField>

          <LineField label="Description" style={{ gridColumn: 'span 2' }}>
            <DescriptionCombobox
              value={row.description}
              onChange={v => setR({ description: v })}
              options={settings.saleLineDescriptions ?? []}
            />
          </LineField>

          {isCert && (
            <>
              <LineField label="Lab">
                <select style={TS} value={row.lab} onChange={e => setR({ lab: e.target.value })}>
                  <option value="">Lab</option>
                  {LABS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </LineField>
              <LineField label="Cert No" style={{ gridColumn: 'span 2', minWidth: 160 }}>
                <input style={{ ...TI, fontFamily: 'var(--font-num)' }}
                  placeholder={row.lab === 'None' ? 'n/a' : 'Cert No'} disabled={row.lab === 'None'}
                  value={row.certificateNo} onChange={e => setR({ certificateNo: e.target.value })} />
              </LineField>
            </>
          )}

          <LineField label="Shape *">
            <select style={TS} value={row.shape} onChange={e => setR({ shape: e.target.value })}>
              <option value="">Shape</option>
              {SHAPES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </LineField>

          <LineField label="Carat *">
            <input className="no-spinner" style={{ ...TI, textAlign: 'right' }} type="number" step="0.01" min="0" placeholder="0.00"
              value={row.carat} onChange={e => setR({ carat: e.target.value })} />
          </LineField>

          <LineField label="Color *">
            <select style={TS} value={row.color} onChange={e => setR({ color: e.target.value })}>
              <option value="">Col</option>
              {colorList.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </LineField>

          <LineField label="Clarity *">
            <select style={TS} value={row.clarity} onChange={e => setR({ clarity: e.target.value })}>
              <option value="">Clr</option>
              {clarityList.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </LineField>

          {isCert && (
            <>
              <LineField label="Cut">
                <select style={TS} value={row.cut} onChange={e => setR({ cut: e.target.value })}>
                  <option value="">Cut</option>
                  {CUT_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </LineField>
              <LineField label="Polish">
                <select style={TS} value={row.polish} onChange={e => setR({ polish: e.target.value })}>
                  <option value="">Polish</option>
                  {CUT_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </LineField>
              <LineField label="Symmetry">
                <select style={TS} value={row.symmetry} onChange={e => setR({ symmetry: e.target.value })}>
                  <option value="">Symmetry</option>
                  {CUT_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </LineField>
              <LineField label="Fluorescence">
                <select style={TS} value={row.fluorescence} onChange={e => setR({ fluorescence: e.target.value })}>
                  <option value="">Fluor.</option>
                  {FLUORESCENCE_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </LineField>
              <LineField label="Measurements" style={{ gridColumn: 'span 2', minWidth: 144 }}>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input style={{ ...TI, minWidth: 0, textAlign: 'center' }} placeholder="L"
                    value={row.measLength} onChange={e => setR({ measLength: e.target.value })} />
                  <span style={{ color: 'var(--text-3)', fontSize: 11 }}>-</span>
                  <input style={{ ...TI, minWidth: 0, textAlign: 'center' }} placeholder="W"
                    value={row.measWidth} onChange={e => setR({ measWidth: e.target.value })} />
                  <span style={{ color: 'var(--text-3)', fontSize: 11 }}>x</span>
                  <input style={{ ...TI, minWidth: 0, textAlign: 'center' }} placeholder="D"
                    value={row.measDepth} onChange={e => setR({ measDepth: e.target.value })} />
                </div>
              </LineField>
            </>
          )}

          {!isCert && (
            <>
              <LineField label="Size">
                <input style={TI} placeholder="+2" value={row.size} onChange={e => setR({ size: e.target.value })} />
              </LineField>
              <LineField label="Sub Size">
                <input style={TI} placeholder="+1.5" value={row.subSize} onChange={e => setR({ subSize: e.target.value })} />
              </LineField>
              <LineField label="SKU *">
                <input style={TI} placeholder="JEW1" value={row.sku} onChange={e => setR({ sku: e.target.value })} />
              </LineField>
            </>
          )}

          {isCert && row.lab !== 'None' && (
            <>
              <LineField label="List Price/ct">
                <input className="no-spinner" style={TI} type="number" step="0.01" min="0" placeholder="0.00"
                  value={row.listPriceAmount} onChange={e => updateListPrice(e.target.value)} />
              </LineField>
              <LineField label="Discount %">
                <input className="no-spinner" style={TI} type="number" step="0.1" placeholder="−30"
                  value={row.priceListPercent} onChange={e => updateDiscount(e.target.value)} />
              </LineField>
            </>
          )}

          <LineField label="Cost/ct">
            <input className="no-spinner" style={TI} type="number" step="0.01" min="0" placeholder="0.00"
              value={row.pricePerCt} onChange={e => updatePrice(e.target.value)} />
          </LineField>

          <LineField label="Total Amount" style={{ gridColumn: 'span 2', minWidth: 160 }}>
            <div className="line-item-readonly">{calc.amount > 0 ? money(calc.amount) : '—'}</div>
          </LineField>
        </div>
      </div>
    </Modal>
  );
}

// ── Return-to-supplier modal ────────────────────────────────────────────
function ReturnLineModal({ memo, line, onClose }: {
  memo: SupplierMemo; line: SupplierMemoLineItem; onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const [date, setDate] = useState(todayISO());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function run() {
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }
    const memoRef = doc(db, 'supplierMemos', memo.id);
    // Legacy in-flight lines (received before Memo In stopped creating a
    // shadow inventory doc) still have one to update; new-style lines don't.
    const itemRef = line.inventoryItemId ? doc(db, 'inventory', line.inventoryItemId) : null;
    try {
      await runTransaction(db, async tx => {
        const [ms, is] = await Promise.all([tx.get(memoRef), itemRef ? tx.get(itemRef) : Promise.resolve(null)]);
        if (!ms.exists()) throw new Error('That memo no longer exists.');
        const m = ms.data() as SupplierMemo;
        const cur = m.lines.find(l => l.id === line.id);
        if (!cur || cur.status !== 'held') throw new Error('That stone is no longer held on this memo.');

        if (itemRef && is?.exists()) {
          tx.update(itemRef, stripUndef({
            status: 'returned', returnedDate: date, returnReason: reason.trim() || undefined,
            updatedAt: serverTimestamp(),
          }));
        }
        const newLines = m.lines.map(l => l.id === line.id
          ? { ...l, status: 'returned' as const, returnedDate: date, returnReason: reason.trim() || undefined }
          : l);
        const held = round2(newLines.filter(l => l.status === 'held').reduce((s, l) => s + (l.costUSD ?? 0), 0));
        tx.update(memoRef, stripUndef({
          lines: newLines.map(l => stripUndef(l)), heldCostUSD: held,
          status: held > 0.005 ? 'open' : 'closed', updatedAt: serverTimestamp(),
        }));

        if (held > 0.005) {
          postEntry(tx, db, buildEntry({
            businessId: business.id, sourceType: 'memo_in', sourceId: memo.id,
            sourceCollection: 'supplierMemos', date: m.date, currency: settings.currency, createdBy: user!.id,
            note: `Memo in ${m.memoNo}`, lines: linesMemoIn(held),
          }), entryId('memo_in', memo.id));
        } else {
          tx.delete(doc(db, 'journalEntries', entryId('memo_in', memo.id)));
        }
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the return.');
    }
    setSaving(false);
  }

  return (
    <Modal open onClose={onClose} title="Return to Supplier"
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={run} loading={saving}>Return Stone</Button>
      </>}>
      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
      <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
        Sending <strong>{line.code ? `${line.code} · ` : ''}{line.shape} {line.carat.toFixed(2)}ct</strong> back — it leaves stock and the
        consignment shrinks. Nothing is owed.
      </div>
      <div className="form-row-2">
        <FormField label="Return date" required>
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
        </FormField>
        <FormField label="Reason (optional)">
          <input value={reason} onChange={e => setReason(e.target.value)} />
        </FormField>
      </div>
    </Modal>
  );
}

// ── Detail modal ────────────────────────────────────────────────────────
const LINE_BADGE: Record<SupplierMemoLineItem['status'], { bg: string; color: string; label: string }> = {
  held:      { bg: 'var(--primary-light)', color: 'var(--primary)', label: 'Held' },
  purchased: { bg: 'var(--success-bg)', color: 'var(--success)', label: 'Purchased' },
  returned:  { bg: 'var(--surface-2)', color: 'var(--text-3)', label: 'Returned' },
};

function LotPill({ children }: { children: ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
      fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 5, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}
function MiniFact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)' }}>{value}</div>
    </div>
  );
}
function VDivider() {
  return <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)' }} />;
}
function FactPanel({ children }: { children: ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 999, padding: '7px 18px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      {children}
    </div>
  );
}
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {children}
    </div>
  );
}

function DetailModal({ memo, supplierName, entities, onClose, onDelete }: {
  memo: SupplierMemo; supplierName: string; entities: Entity[]; onClose: () => void; onDelete: () => void;
}) {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const [action, setAction] = useState<{ kind: 'convert' | 'return'; line: SupplierMemoLineItem } | null>(null);
  const allHeld = memo.lines.every(l => l.status === 'held');
  const returnOverdue = memo.status === 'open' && !!memo.returnByDate
    && memo.returnByDate < new Date().toLocaleDateString('en-CA');

  return (
    <>
      <Modal
        open onClose={onClose} size="2xl" hideHead
        footer={
          <div style={{ display: 'flex', width: '100%', gap: 8 }}>
            {allHeld && <Button variant="danger" onClick={onDelete}>Delete Consignment</Button>}
            <Button variant="secondary" onClick={onClose} style={{ marginLeft: 'auto' }}>Close</Button>
          </div>
        }
      >
        <ModalBanner
          title={supplierName}
          eyebrow="Consignment"
          right={
            <FactPanel>
              <div style={{ textAlign: 'right' }} title={memo.memoNo}>
                <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
                  Consignment No
                </div>
                <LotPill>{memoNoDisplay(memo.memoNo)}</LotPill>
              </div>
              <VDivider />
              <MiniFact label="Received" value={fmtDate(memo.date)} />
              {memo.returnByDate && (
                <>
                  <VDivider />
                  <MiniFact
                    label="Return By"
                    value={<span style={{ color: returnOverdue ? 'var(--danger)' : undefined }}>{fmtDate(memo.returnByDate)}</span>}
                  />
                </>
              )}
              <VDivider />
              <span className="badge" style={{
                fontSize: 10, fontWeight: 700,
                background: memo.status === 'open' ? 'var(--primary-light)' : 'var(--surface-2)',
                color: memo.status === 'open' ? 'var(--primary)' : 'var(--text-3)',
              }}>{memo.status === 'open' ? 'Open' : 'Closed'}</span>
            </FactPanel>
          }
          onClose={onClose}
        />

        <SectionLabel>Consignment Summary</SectionLabel>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 1,
          border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
          overflow: 'hidden', marginBottom: 20,
        }}>
          {[
            { label: 'Stones', val: String(memo.lines.length), color: 'var(--text-1)' },
            { label: 'Total Carat', val: `${memo.lines.reduce((s, l) => s + (l.carat ?? 0), 0).toFixed(2)} ct`, color: 'var(--text-1)' },
            { label: 'Total Value', val: money(memo.totalCostUSD ?? 0), color: 'var(--text-1)' },
            { label: 'Still Held', val: money(heldOf(memo)), color: 'var(--primary)' },
            { label: 'Purchased', val: money(memo.lines.filter(l => l.status === 'purchased').reduce((s, l) => s + (l.costUSD ?? 0), 0)), color: 'var(--success)' },
            { label: 'Returned', val: money(memo.lines.filter(l => l.status === 'returned').reduce((s, l) => s + (l.costUSD ?? 0), 0)), color: 'var(--text-3)' },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ background: 'var(--surface-2)', padding: '12px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginBottom: 4 }}>{label}</div>
              <div style={{ fontWeight: 700, fontFamily: 'var(--font-num)', fontSize: 14, color }}>{val}</div>
            </div>
          ))}
        </div>

        {(memo.supplierRef || memo.notes) && (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-2)', marginBottom: 16 }}>
            {memo.supplierRef && <span>Their ref <strong style={{ fontFamily: 'var(--font-num)' }}>{memo.supplierRef}</strong></span>}
            {memo.notes && <span>Notes <strong>{memo.notes}</strong></span>}
          </div>
        )}

        <SectionLabel>Stones</SectionLabel>
        <div className="table-wrap" style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-sm)' }}>
          <table>
            <thead>
              <tr>
                <th>Stone</th>
                <th>Lab / Cert</th>
                <th style={{ textAlign: 'right', width: 70 }}>Carat</th>
                <th style={{ textAlign: 'right', width: 110 }} title="The supplier's price per ct">Price/ct</th>
                <th style={{ textAlign: 'right', width: 110 }}>Amount</th>
                <th style={{ width: 130 }}>Status</th>
                <th style={{ width: 190 }} />
              </tr>
            </thead>
            <tbody>
              {memo.lines.map(l => {
                const b = LINE_BADGE[l.status];
                const costPerCt = l.carat > 0 ? (l.costUSD ?? 0) / l.carat : 0;
                return (
                  <tr key={l.id}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {l.code && <span style={{ fontFamily: 'var(--font-num)', background: 'var(--primary-light)', color: 'var(--primary)', padding: '3px 8px', borderRadius: 6, marginRight: 6, fontSize: 11, fontWeight: 700 }}>{l.code}</span>}
                      {l.shape} {l.color} {l.clarity}
                      {l.size && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-3)' }}>{l.size}</span>}
                      {l.status === 'purchased' && l.purchaseInvoiceNo && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-3)' }}>→ {l.purchaseInvoiceNo}</span>}
                      {l.status === 'returned' && l.returnedDate && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-3)' }}>returned {fmtDate(l.returnedDate)}</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>
                      {l.lab ? `${l.lab}${l.certificateNo ? ` · ${l.certificateNo}` : ''}` : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)' }}>{l.carat.toFixed(2)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)' }}>{money(costPerCt)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-1)' }}>{money(l.costUSD ?? 0)}</td>
                    <td><span className="badge" style={{ fontSize: 10, fontWeight: 700, background: b.bg, color: b.color }}>{b.label}</span></td>
                    <td>
                      {l.status === 'held' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button variant="primary" size="sm" onClick={() => setAction({ kind: 'convert', line: l })}>Convert to Purchase</Button>
                          <Button variant="ghost" size="sm" onClick={() => setAction({ kind: 'return', line: l })}>Return</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: 'var(--surface-2)', borderTop: '2px solid var(--border)' }}>
                <td colSpan={2} style={{ fontWeight: 600, fontSize: 12 }}>Total</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 12 }}>
                  {memo.lines.reduce((s, l) => s + (l.carat ?? 0), 0).toFixed(2)}
                </td>
                <td />
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 12 }}>{money(memo.totalCostUSD ?? 0)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      </Modal>

      {action?.kind === 'convert' && <ConvertModal memo={memo} line={action.line} supplierName={supplierName} entities={entities} onClose={() => setAction(null)} />}
      {action?.kind === 'return' && <ReturnLineModal memo={memo} line={action.line} onClose={() => setAction(null)} />}
    </>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
export default function MemoInPage() {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [memos, setMemos] = useState<SupplierMemo[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);

  const [dateKey, setDateKey] = useState<DateFilterKey>('this-year');
  const [searchQ, setSearchQ] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [showReceive, setShowReceive] = useState(false);
  // Dashboard "+ New" opens this form via ?new=1.
  useNewParam(() => setShowReceive(true));
  // Excel import: the import modal, and (single consignment) the Receive form pre-filled from it.
  const [showImport, setShowImport] = useState(false);
  const [imported, setImported] = useState<{ rows: MemoRow[]; fileName: string; header?: Partial<MemoHeader> } | null>(null);
  const [importedNos, setImportedNos] = useState<string[]>([]);
  const [detail, setDetail] = useState<SupplierMemo | null>(null);
  const [confirmDel, setConfirmDel] = useState<SupplierMemo | null>(null);
  const [busy, setBusy] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const u1 = onSnapshot(collection(db, 'supplierMemos'),
      snap => { setMemos(snap.docs.map(d => ({ id: d.id, ...d.data() } as SupplierMemo))); setLoading(false); },
      () => setLoading(false));
    const u2 = onSnapshot(collection(db, 'entities'),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))), () => {});
    return () => { u1(); u2(); };
  }, [business]);

  const entityName = (id: string) => entities.find(e => e.id === id)?.companyName ?? '—';
  const window = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);
  // keep the open detail modal in sync with live snapshots
  const liveDetail = detail ? memos.find(m => m.id === detail.id) ?? null : null;

  const baseRows = useMemo(() => {
    if (!window) return [];
    return memos
      .filter(m => inWindow(m.date, window))
      .filter(m => {
        const q = searchQ.trim().toLowerCase();
        return !q || [m.memoNo, m.supplierRef, entityName(m.fromEntityId)].some(v => (v ?? '').toLowerCase().includes(q));
      })
      .sort((a, b) => b.date.localeCompare(a.date) || toMillis(b.createdAt) - toMillis(a.createdAt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memos, window, searchQ, entities]);

  // Excel-style filters (replaced the header Status menu; opens on Open, as before).
  const filterColumns = useMemo<FilterColumn<SupplierMemo>[]>(() => [
    { key: 'memo',     label: 'Memo No',  get: m => memoNoDisplay(m.memoNo) },
    { key: 'month',    label: 'Received', get: m => m.date ? new Date(m.date + 'T00:00:00').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '',
      sort: (a, b) => new Date('1 ' + a).getTime() - new Date('1 ' + b).getTime() },
    { key: 'supplier', label: 'Supplier', get: m => entityName(m.fromEntityId) },
    { key: 'returnBy', label: 'Return By', get: m => {
      if (!m.returnByDate || memoStats(m).heldN === 0) return '';
      const d = daysUntil(m.returnByDate);
      return d < 0 ? 'Overdue' : d <= 7 ? 'Due within a week' : 'Later';
    }, sort: (a, b) => ['Overdue', 'Due within a week', 'Later'].indexOf(a) - ['Overdue', 'Due within a week', 'Later'].indexOf(b) },
    { key: 'status',   label: 'Status',   get: m => (m.status === 'open' ? 'Open' : 'Closed') },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entities]);
  const { rows, filters: colFilters } = useColumnFilters(baseRows, filterColumns, { status: ['Open'] });
  const onlyDefault = colFilters.active.length === 1 && colFilters.active[0].key === 'status'
    && colFilters.active[0].values.length === 1 && colFilters.active[0].values[0] === 'Open';

  useEffect(() => { setCurrentPage(1); }, [dateKey, customFrom, customTo, searchQ, colFilters.stamp]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Print covers every filtered row, not just this page (same as the other list pages).
  // globalThis, not window: `window` here is the page's date range.
  const [printAll, setPrintAll] = useState(false);
  useEffect(() => {
    if (!printAll) return;
    let cancelled = false;
    const after = () => setPrintAll(false);
    globalThis.addEventListener('afterprint', after);
    requestAnimationFrame(() => { if (!cancelled) requestAnimationFrame(() => { if (!cancelled) globalThis.print(); }); });
    return () => { cancelled = true; globalThis.removeEventListener('afterprint', after); };
  }, [printAll]);

  function handleExportCSV() {
    downloadCSV('memo-in',
      ['Memo No', 'Received', 'Supplier', 'Their Ref', 'Return By', 'Total Ct', 'Total Amount', 'Purchased', 'Returned', 'Status'],
      rows.map(m => { const st = memoStats(m); return [m.memoNo, m.date, entityName(m.fromEntityId), m.supplierRef ?? '', m.returnByDate ?? '', st.totalCt.toFixed(2), st.totalAmt.toFixed(2), st.boughtAmt.toFixed(2), st.returnedAmt.toFixed(2), m.status]; }),
    );
  }

  const kpi = useMemo(() => {
    const held = memos.reduce((s, m) => s + heldOf(m), 0);
    const openCount = memos.filter(m => m.status === 'open').length;
    // Carats (and stones) still physically with us — 'held' lines only, across every memo.
    const heldLines = memos.flatMap(m => (m.lines ?? []).filter(l => l.status === 'held'));
    const heldCt = round2(heldLines.reduce((s, l) => s + (l.carat ?? 0), 0));
    // Open memos past their Return By date with stones still held.
    const overdue = memos.filter(m => m.returnByDate && memoStats(m).heldN > 0 && daysUntil(m.returnByDate) < 0).length;
    return { held, openCount, heldCt, heldStones: heldLines.length, overdue };
  }, [memos]);

  async function doDelete(m: SupplierMemo) {
    setBusy(true);
    const db = getClientDb();
    if (!db || !business) { setBusy(false); return; }
    try {
      await runTransaction(db, async tx => {
        const memoRef = doc(db, 'supplierMemos', m.id);
        const ms = await tx.get(memoRef);
        if (!ms.exists()) return;
        const fresh = ms.data() as SupplierMemo;
        if (fresh.lines.some(l => l.status !== 'held')) {
          throw new Error('Some stones have been bought or returned — this memo can no longer be deleted.');
        }
        // New-style lines never had an inventory doc to begin with; only
        // legacy in-flight lines (received before this model) do.
        const snaps = await Promise.all(fresh.lines.map(l => l.inventoryItemId ? tx.get(doc(db, 'inventory', l.inventoryItemId)) : Promise.resolve(null)));
        fresh.lines.forEach((l, i) => {
          if (l.inventoryItemId && snaps[i]?.exists()) {
            tx.delete(doc(db, 'inventory', l.inventoryItemId));
            if (l.code) tx.delete(doc(db, 'itemCodes', normalizeCode(l.code)));
          }
        });
        tx.delete(doc(db, 'journalEntries', entryId('memo_in', m.id)));
        tx.delete(memoRef);
      });
      setDetail(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Could not delete the memo.');
    } finally { setBusy(false); setConfirmDel(null); }
  }

  return (
    <AppLayout
      title="Memo In"
      subtitle="Stones a supplier has consigned to you — held, not owned, until you buy or return each one."
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Memo no, supplier, their ref…" label="Search consignments" />
        <DateRangeButton
          value={dateKey} onChange={setDateKey} defaultKey="this-year"
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
        />
        {(searchQ || !onlyDefault || dateKey !== 'this-year') && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); setDateKey('this-year'); colFilters.clear(); colFilters.set('status', ['Open']); }}>Clear</button>
        )}
      </>}
      headerActions={<>
        {/* One joined group, same order as Purchase: Import · Export. */}
        <div className="tb-group">
          <button type="button" className="tb-group-btn" title="Import from Excel" aria-label="Import from Excel"
            disabled={!business} onClick={() => { setImportedNos([]); setShowImport(true); }}>
            <IconImportSm />
          </button>
          <Dropdown
            align="right"
            trigger={() => (
              <button type="button" className="tb-group-btn" title="Export" aria-label="Export"><IconExportSm /></button>
            )}
          >
            <button type="button" className="dropdown-item" onClick={handleExportCSV}>Export CSV</button>
            <button type="button" className="dropdown-item" onClick={() => setPrintAll(true)}>Print List</button>
          </Dropdown>
        </div>
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => { setImported(null); setShowReceive(true); }} disabled={!business}
          style={{ height: 34, boxSizing: 'border-box' }}>Receive Consignment</Button>
      </>}
    >
      {/* What you're holding right now, whatever the filters say. */}
      <StatChips items={[
        { label: 'Held value', value: money(kpi.held), tone: 'primary' },
        { label: 'Carats held', value: `${kpi.heldCt.toFixed(2)} ct · ${kpi.heldStones} stone${kpi.heldStones === 1 ? '' : 's'}` },
        { label: 'Open', value: String(kpi.openCount) },
        ...(kpi.overdue > 0 ? [{ label: 'Return overdue', value: String(kpi.overdue), tone: 'danger' as const }] : []),
      ]} end={colFilters.active.length > 0 ? <ColumnFilterChips filters={colFilters} inline /> : undefined} />

      {importedNos.length > 0 && (
        <div className="msg msg-info" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1 }}>
            Received {importedNos.length} consignment{importedNos.length === 1 ? '' : 's'}: <strong>{importedNos.join(', ')}</strong>
          </span>
          <button type="button" className="line-item-link-btn" onClick={() => setImportedNos([])}>Dismiss</button>
        </div>
      )}

      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : rows.length === 0 && baseRows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No consignments here</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Use “Receive Consignment” to log stones a supplier sent you on memo.</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th className="th-filterable" style={{ width: 110 }}>Memo No<ColumnFilterButton filters={colFilters} colKey="memo" label="Memo No" /></th>
                  <th className="th-filterable" style={{ width: 120 }}>Received<ColumnFilterButton filters={colFilters} colKey="month" label="Received" /></th>
                  <th className="th-filterable">Supplier<ColumnFilterButton filters={colFilters} colKey="supplier" label="Supplier" /></th>
                  <th>Their Ref</th>
                  <th className="th-filterable" style={{ width: 150 }}>Return By<ColumnFilterButton filters={colFilters} colKey="returnBy" label="Return By" /></th>
                  <th style={{ textAlign: 'right' }}>Total Ct</th>
                  <th style={{ textAlign: 'right' }}>Total Amount</th>
                  <th style={{ textAlign: 'right' }}>Purchased</th>
                  <th style={{ textAlign: 'right' }}>Returned</th>
                  <th className="th-filterable" style={{ width: 100 }}>Status<ColumnFilterButton filters={colFilters} colKey="status" label="Status" /></th>
                </tr>
              </thead>
              <tbody>
                {(printAll ? rows : pagedRows).map(m => {
                  const s = memoStats(m);
                  const plain: CSSProperties = { fontSize: 12, color: 'var(--text-2)' };
                  const numCell: CSSProperties = { ...plain, textAlign: 'right', fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' };
                  const dash = <span style={{ opacity: 0.4 }}>—</span>;
                  // Return-by urgency only matters while stones are still held.
                  const due = m.returnByDate && s.heldN > 0 ? daysUntil(m.returnByDate) : null;
                  const dueColor = due == null ? undefined : due < 0 ? 'var(--danger)' : due <= 7 ? 'var(--warning)' : undefined;
                  return (
                    <tr key={m.id} onClick={() => setDetail(m)} style={{ cursor: 'pointer' }}>
                      <td>
                        <span title={m.memoNo} style={{
                          background: 'var(--primary-light)', color: 'var(--primary)',
                          fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
                          padding: '3px 8px', borderRadius: 6, letterSpacing: '0.02em',
                        }}>{memoNoDisplay(m.memoNo)}</span>
                      </td>
                      <td style={{ ...plain, fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>{fmtDate(m.date)}</td>
                      <td style={plain}>{entityName(m.fromEntityId) || dash}</td>
                      <td style={{ ...plain, fontFamily: 'var(--font-num)' }}>{m.supplierRef || dash}</td>
                      <td style={{ ...plain, fontFamily: 'var(--font-num)', whiteSpace: 'nowrap', color: dueColor ?? plain.color }}
                        title={due == null ? undefined : due < 0 ? `${-due} day${due === -1 ? '' : 's'} overdue` : `in ${due} day${due === 1 ? '' : 's'}`}>
                        {m.returnByDate ? fmtDate(m.returnByDate) : dash}
                        {due != null && due < 0 && <span style={{ fontSize: 10, fontWeight: 700, marginLeft: 6 }}>Overdue</span>}
                      </td>
                      <td style={numCell}>{s.totalCt.toFixed(2)}</td>
                      <td style={numCell}>{money(s.totalAmt)}</td>
                      <td style={numCell}>{s.boughtAmt ? money(s.boughtAmt) : dash}</td>
                      <td style={numCell}>{s.returnedAmt ? money(s.returnedAmt) : dash}</td>
                      <td>
                        <span className="badge" style={{
                          fontSize: 10, fontWeight: 700,
                          background: m.status === 'open' ? 'var(--primary-light)' : 'var(--surface-2)',
                          color: m.status === 'open' ? 'var(--primary)' : 'var(--text-3)',
                        }}>{m.status === 'open' ? 'Open' : 'Closed'}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <Pagination
          current={currentPage} total={totalPages}
          totalItems={rows.length} pageSize={PAGE_SIZE}
          onChange={setCurrentPage}
        />
      )}

      {showReceive && (
        <ReceiveModal
          entities={entities}
          initialRows={imported?.rows} initialHeader={imported?.header} importedFrom={imported?.fileName}
          onClose={() => { setShowReceive(false); setImported(null); }}
        />
      )}
      {showImport && (
        <ImportMemoInModal
          entities={entities}
          onClose={() => setShowImport(false)}
          onReview={(rows, fileName, header) => { setShowImport(false); setImported({ rows, fileName, header }); setShowReceive(true); }}
          onImported={(nos, done) => { setImportedNos(prev => [...prev, ...nos]); if (done) setShowImport(false); }}
        />
      )}
      {liveDetail && (
        <DetailModal
          memo={liveDetail}
          supplierName={entityName(liveDetail.fromEntityId)}
          entities={entities}
          onClose={() => setDetail(null)}
          onDelete={() => setConfirmDel(liveDetail)}
        />
      )}
      {confirmDel && (
        <ConfirmModal
          open title="Delete this consignment?"
          message={`${confirmDel.memoNo} and its ${confirmDel.lines.length} held stone${confirmDel.lines.length === 1 ? '' : 's'} will be removed from stock. Only possible while nothing has been bought or returned.`}
          confirmLabel="Delete" loading={busy}
          onConfirm={() => doDelete(confirmDel)} onClose={() => setConfirmDel(null)}
        />
      )}
    </AppLayout>
  );
}
